import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  Camera,
  CheckCircle2,
  ChevronRight,
  CircleGauge,
  Download,
  Droplets,
  Fish,
  FlaskConical,
  Images,
  Loader2,
  Maximize2,
  Minimize2,
  Pause,
  Play,
  RotateCcw,
  ScanLine,
  Share2,
  SkipBack,
  SkipForward,
  Thermometer,
  Waves,
  Wifi,
  WifiOff,
  Zap
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import { Button } from './ui/button';
import { AqlVisionMark } from './branding/aql-vision-brand';
import { AqlAssistantButton } from './aql-assistant/AqlAssistantButton';
import { AqlAssistantPanel } from './aql-assistant/AqlAssistantPanel';
import { AqlOperationalContextProvider } from './aql-assistant/AqlOperationalContextProvider';
import type { AqlAssistantLocalSummary } from './aql-assistant/useAqlAssistant';
import {
  DEMO_AUTH_TOKEN,
  DEMO_DETECTION_TIMELINE,
  DEMO_VIDEO_DURATION_SECONDS,
  getDemoDetectionTime,
  getDemoVideoSources
} from '@/app/utils/vision-demo-data';
import {
  fetchDetectionCount,
  fetchCameraTrainingBurstState,
  fetchCameraTrainingDataset,
  fetchLatestCapture,
  fetchLatestSensorReading,
  fetchLiveDevicePresence,
  fetchLiveCameraFeeds,
  hasActiveUltrasoundEquipment,
  resolveActiveZone,
  startCameraTrainingBurst,
  stopCameraTrainingBurst,
  type LiveCapture,
  type LiveCameraFeed,
  type LiveDevicePresence,
  type LiveSensorReading,
  type LiveTrainingDatasetExport,
  type LiveTrainingBurstState,
  type LiveZoneContext
} from '@/app/services/vision-live-service';
import { isHeartbeatOnline } from '@/app/utils/device-presence';
import { runBrowserOnnxInference, type BrowserInferenceResult } from '@/app/services/browser-onnx-inference';
import { loadLatestEdgeInference, type LatestEdgeInference } from '@/app/services/kit-vision-runtime-service';
import { downloadBlob, fetchRemoteFilesAsZip } from '@/app/utils/browser-zip';
import { supabase } from '@/utils/supabase/client';

const LIVE_CAPTURE_REFRESH_MS = 30_000;
const LIVE_SENSORS_REFRESH_MS = 5_000;
const LIVE_FEED_FRAME_REFRESH_MS = 1_000;
const LIVE_FEED_BUFFER_FRAMES = 2;
const LIVE_FRAME_STALE_MS = 30_000;
const LIVE_FRAME_LOAD_STALE_MS = 5_000;
const LIVE_INFERENCE_STALE_MS = 15_000;
const liveFeedKey = (feed: LiveCameraFeed) => feed.cameraCode ?? feed.cameraId ?? feed.path;

type AlertLevel = 'normal' | 'attention' | 'critical';
type MetricKey = 'temperature' | 'ph' | 'oxygen' | 'salinity';
type Injection = 'thermal' | 'oxygen' | 'sensor';

const timestampMs = (value: string | null | undefined) => {
  if (!value) return null;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
};

const isRecentTimestamp = (value: string | null | undefined, nowMs: number, staleMs: number) => {
  const parsed = timestampMs(value);
  return parsed !== null && nowMs - parsed >= 0 && nowMs - parsed <= staleMs;
};

const cameraFeedOnline = (feed: LiveCameraFeed, nowMs: number, loadedAtMs?: number | null) => {
  const status = String(feed.lastFrameStatus ?? '').trim().toLowerCase();
  const hasRecentMetadata = isRecentTimestamp(feed.lastFrameAt, nowMs, LIVE_FRAME_STALE_MS);
  const hasRecentLoadedFrame = typeof loadedAtMs === 'number' && nowMs - loadedAtMs >= 0 && nowMs - loadedAtMs <= LIVE_FRAME_LOAD_STALE_MS;
  if (['error', 'failed', 'offline', 'inactive'].includes(status)) return hasRecentLoadedFrame;
  return hasRecentMetadata || hasRecentLoadedFrame;
};

const formatTimeOnly = (value: string | null | undefined) => {
  const parsed = timestampMs(value);
  if (parsed === null) return null;
  return new Date(parsed).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const formatTimeWithMilliseconds = (value: string | null | undefined) => {
  const parsed = timestampMs(value);
  if (parsed === null) return null;
  const date = new Date(parsed);
  const time = date.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  return `${time}.${String(date.getMilliseconds()).padStart(3, '0')}`;
};

interface VisionDemoProps {
  onBack: () => void;
  onOpenAlerts?: (alertId?: string) => void;
  initialAlertId?: string | null;
  /** When false, the screen pulls live data from Supabase instead of demo assets. */
  demoMode?: boolean;
  embedded?: boolean;
  /** Optional client/zone scope used when demoMode is false. */
  clientId?: string | null;
  projectId?: string | null;
  zoneId?: string | null;
}

interface VisualEvent {
  id: string;
  start: number;
  end: number;
  title: string;
  confidence: number;
  box: {
    left: string;
    top: string;
    width: string;
    height: string;
  };
}

interface DemoAlert {
  id: string;
  title: string;
  detail: string;
  level: AlertLevel;
  timestamp: string;
}

type TrainingExportAction = 'download' | 'share';

interface TrainingExportPrompt {
  cameraId: string | null;
  cameraCode: string | null;
  cameraName: string | null;
  folderPath: string | null;
  count: number;
  target: number;
  completedAt: string | null;
}

const DEMO_VIDEO_SOURCES = getDemoVideoSources();
const DEMO_VIDEO_WEBM_SRC = DEMO_VIDEO_SOURCES.webm;
const DEMO_VIDEO_MP4_SRC = DEMO_VIDEO_SOURCES.mp4;
const DEMO_POSTER_SRC = DEMO_VIDEO_SOURCES.poster;

const withCacheBuster = (url: string, value: number) =>
  `${url}${url.includes('?') ? '&' : '?'}t=${value}`;

const safeArchivePart = (value: unknown) =>
  String(value ?? 'camera')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 80) || 'camera';

const trainingArchiveFileName = (prompt: TrainingExportPrompt) => {
  const stamp = new Date(prompt.completedAt ?? Date.now()).toISOString().replace(/[:.]/g, '-');
  return `aql-training-${safeArchivePart(prompt.cameraCode ?? prompt.cameraName)}-${stamp}.zip`;
};

const buildTrainingShareText = (dataset: LiveTrainingDatasetExport, prompt: TrainingExportPrompt) => {
  const title = prompt.cameraName ?? prompt.cameraCode ?? 'camara';
  const urls = dataset.images.slice(0, 15).map((image) => image.url);
  const remaining = dataset.images.length - urls.length;
  return [
    `AQL Vision - recolha de treino ${title}`,
    `${dataset.images.length} imagens em ${dataset.folderPath ?? prompt.folderPath ?? 'Supabase Storage'}.`,
    ...urls,
    remaining > 0 ? `Mais ${remaining} imagens disponiveis na pasta Supabase.` : ''
  ].filter(Boolean).join('\n');
};

const copyTextToClipboard = async (text: string) => {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.style.position = 'fixed';
  textArea.style.left = '-9999px';
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  document.execCommand('copy');
  textArea.remove();
};

const VISUAL_EVENTS: VisualEvent[] = [
  {
    id: 'demo-visual-lesion-entry',
    start: 0,
    end: 3.4,
    title: 'Suspeita visual: zona avermelhada',
    confidence: 78,
    box: { left: '20%', top: '16%', width: '13%', height: '7%' }
  },
  {
    id: 'demo-visual-lesion-left-edge',
    start: 14.2,
    end: 18.8,
    title: 'Suspeita visual: lesão periférica',
    confidence: 72,
    box: { left: '0%', top: '38%', width: '9%', height: '6%' }
  },
  {
    id: 'demo-visual-lesion-late',
    start: 64.1,
    end: 68.1,
    title: 'Suspeita visual: mancha vermelha',
    confidence: 81,
    box: { left: '60%', top: '48%', width: '11%', height: '6%' }
  }
];

const METRIC_CONFIG: Record<MetricKey, {
  label: string;
  unit: string;
  decimals: number;
  target: string;
  icon: typeof Thermometer;
  color: string;
}> = {
  temperature: {
    label: 'Temperatura',
    unit: '°C',
    decimals: 1,
    target: 'alvo 14-18 °C',
    icon: Thermometer,
    color: 'text-orange-600 dark:text-orange-300'
  },
  ph: {
    label: 'pH',
    unit: '',
    decimals: 2,
    target: 'alvo 7.9-8.2',
    icon: FlaskConical,
    color: 'text-sky-600 dark:text-sky-300'
  },
  oxygen: {
    label: 'O₂ dissolvido',
    unit: 'mg/L',
    decimals: 1,
    target: 'alvo >= 6.0 mg/L',
    icon: Droplets,
    color: 'text-emerald-600 dark:text-emerald-300'
  },
  salinity: {
    label: 'Salinidade',
    unit: 'PSU',
    decimals: 1,
    target: 'alvo 25-35 PSU',
    icon: Waves,
    color: 'text-blue-600 dark:text-blue-300'
  }
};

const formatClock = (seconds: number) => {
  const safeSeconds = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(safeSeconds / 60);
  const remainder = safeSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`;
};

const pulse = (time: number, start: number, end: number) => {
  if (time < start || time > end) return 0;
  return Math.sin(((time - start) / (end - start)) * Math.PI);
};

const levelTone = (level: AlertLevel) => {
  if (level === 'critical') return 'border-red-300 bg-red-50 text-red-800 dark:border-red-500/40 dark:bg-red-500/12 dark:text-red-100';
  if (level === 'attention') return 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/12 dark:text-amber-100';
  return 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-500/35 dark:bg-emerald-500/10 dark:text-emerald-100';
};

const metricLevel = (key: MetricKey, value: number): AlertLevel => {
  if (key === 'temperature') {
    if (value < 11 || value > 23) return 'critical';
    if (value < 14 || value > 18) return 'attention';
    return 'normal';
  }
  if (key === 'ph') {
    if (value < 7.6 || value > 8.4) return 'critical';
    if (value < 7.9 || value > 8.2) return 'attention';
    return 'normal';
  }
  if (key === 'oxygen') {
    if (value < 4) return 'critical';
    if (value < 6) return 'attention';
    return 'normal';
  }
  if (value < 25 || value > 35) return 'critical';
  if (value < 28 || value > 33) return 'attention';
  return 'normal';
};

const buildTelemetry = (time: number, injection: Injection | null) => {
  const thermalPulse = pulse(time, 24, 36);
  const oxygenPulse = pulse(time, 42, 55);
  const phPulse = pulse(time, 57, 67);

  const injectedThermal = injection === 'thermal' ? 4.8 : 0;
  const injectedOxygen = injection === 'oxygen' ? 2.6 : 0;

  const temperature = 16.1 + Math.sin(time / 5.5) * 0.32 + Math.sin(time / 2.8) * 0.14 + thermalPulse * 3.15 + injectedThermal;
  const ph = 7.98 + Math.sin(time / 7.2) * 0.04 + phPulse * 0.34;
  const oxygen = 6.85 + Math.cos(time / 6.3) * 0.22 - oxygenPulse * 2.05 - injectedOxygen;
  const salinity = 30.5 + Math.sin(time / 10) * 0.35;

  return {
    temperature: Number(temperature.toFixed(2)),
    ph: Number(ph.toFixed(3)),
    oxygen: Number(Math.max(1.8, oxygen).toFixed(2)),
    salinity: Number(salinity.toFixed(2))
  };
};

function IconButton({
  label,
  children,
  onClick,
  active = false,
  size = 'md',
  disabled = false
}: {
  label: string;
  children: ReactNode;
  onClick: () => void;
  active?: boolean;
  size?: 'sm' | 'md';
  disabled?: boolean;
}) {
  const dim = size === 'sm' ? 'h-8 w-8' : 'h-9 w-9';
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`inline-flex ${dim} items-center justify-center rounded-lg border transition-colors ${
        disabled
          ? 'cursor-not-allowed border-border bg-muted/40 text-muted-foreground opacity-55'
          : active
          ? 'border-sky-500 bg-sky-600 text-white shadow-sm'
          : 'border-border bg-card text-card-foreground hover:border-primary/40 hover:bg-accent dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:border-sky-500/60 dark:hover:bg-sky-500/12'
      }`}
    >
      {children}
    </button>
  );
}

function StatusPill({ level, children }: { level: AlertLevel; children: ReactNode }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${levelTone(level)}`}>
      {children}
    </span>
  );
}

const ECHOGRAM_WINDOW_SECONDS = 48;
const ECHOGRAM_MAX_DEPTH_M = 2.2;
const ECHOGRAM_RASTER_WIDTH = 560;
const ECHOGRAM_RASTER_HEIGHT = 208;
const ECHOGRAM_PALETTE = [
  { stop: 0, color: [0, 0, 0] },
  { stop: 0.12, color: [3, 18, 50] },
  { stop: 0.26, color: [0, 69, 160] },
  { stop: 0.42, color: [0, 191, 255] },
  { stop: 0.56, color: [60, 214, 76] },
  { stop: 0.7, color: [248, 232, 59] },
  { stop: 0.82, color: [255, 132, 28] },
  { stop: 0.94, color: [218, 35, 26] },
  { stop: 1, color: [255, 255, 255] }
];

interface EchogramEcho {
  time: number;
  depth: number;
  timeSigma: number;
  depthSigma: number;
  strength: number;
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const hashNoise = (x: number, y: number) => {
  const value = Math.sin(x * 12.9898 + y * 78.233) * 43758.5453123;
  return value - Math.floor(value);
};

const echogramBottomDepth = (time: number) => {
  return clamp(
    1.76 + Math.sin(time * 0.18) * 0.075 + Math.sin(time * 0.47 + 1.3) * 0.028,
    1.56,
    1.96
  );
};

const interpolateEkColor = (value: number) => {
  const v = clamp(value, 0, 1);
  for (let i = 1; i < ECHOGRAM_PALETTE.length; i += 1) {
    const previous = ECHOGRAM_PALETTE[i - 1];
    const next = ECHOGRAM_PALETTE[i];
    if (v > next.stop) continue;

    const span = next.stop - previous.stop || 1;
    const t = (v - previous.stop) / span;
    return previous.color.map((channel, index) => Math.round(channel + (next.color[index] - channel) * t));
  }

  return ECHOGRAM_PALETTE[ECHOGRAM_PALETTE.length - 1].color;
};

const buildEchogramEchoes = (time: number, sonarCount: number): EchogramEcho[] => {
  const echoes: EchogramEcho[] = [];
  const slotSeconds = 1.12;
  const firstSlot = Math.floor((time - ECHOGRAM_WINDOW_SECONDS - 4) / slotSeconds);
  const lastSlot = Math.ceil((time + 3) / slotSeconds);
  const density = clamp((sonarCount - 10) / 25, 0.42, 0.92);

  for (let slot = firstSlot; slot <= lastSlot; slot += 1) {
    if (hashNoise(slot, 11) > density) continue;

    const echoTime = slot * slotSeconds + (hashNoise(slot, 17) - 0.5) * 0.72;
    const bottom = echogramBottomDepth(echoTime);
    const schoolBand = 0.42 + hashNoise(Math.floor(slot / 6), 23) * 0.88 + Math.sin(echoTime * 0.2) * 0.1;
    const depth = clamp(schoolBand + (hashNoise(slot, 31) - 0.5) * 0.34, 0.22, bottom - 0.2);
    const strength = 0.46 + hashNoise(slot, 41) * 0.5;

    echoes.push({
      time: echoTime,
      depth,
      strength,
      timeSigma: 0.11 + hashNoise(slot, 53) * 0.16,
      depthSigma: 0.018 + hashNoise(slot, 59) * 0.026
    });

    if (hashNoise(slot, 67) > 0.78) {
      echoes.push({
        time: echoTime + (hashNoise(slot, 71) - 0.5) * 0.44,
        depth: clamp(depth + (hashNoise(slot, 73) - 0.5) * 0.18, 0.22, bottom - 0.18),
        strength: strength * (0.72 + hashNoise(slot, 79) * 0.22),
        timeSigma: 0.1 + hashNoise(slot, 83) * 0.12,
        depthSigma: 0.016 + hashNoise(slot, 89) * 0.022
      });
    }
  }

  return echoes;
};

const renderEchogram = (canvas: HTMLCanvasElement, time: number, sonarCount: number) => {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const rect = canvas.getBoundingClientRect();
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const width = Math.max(420, Math.round((rect.width || 720) * dpr));
  const height = Math.max(230, Math.round((rect.height || 250) * dpr));

  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width;
    canvas.height = height;
  }

  const padLeft = Math.round(44 * dpr);
  const padRight = Math.round(58 * dpr);
  const padTop = Math.round(18 * dpr);
  const padBottom = Math.round(30 * dpr);
  const plotX = padLeft;
  const plotY = padTop;
  const plotW = width - padLeft - padRight;
  const plotH = height - padTop - padBottom;
  const echoes = buildEchogramEchoes(time, sonarCount);

  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = '#02060b';
  ctx.fillRect(0, 0, width, height);

  const buffer = document.createElement('canvas');
  buffer.width = ECHOGRAM_RASTER_WIDTH;
  buffer.height = ECHOGRAM_RASTER_HEIGHT;
  const bufferCtx = buffer.getContext('2d');
  if (!bufferCtx) return;

  const image = bufferCtx.createImageData(ECHOGRAM_RASTER_WIDTH, ECHOGRAM_RASTER_HEIGHT);
  const data = image.data;

  for (let x = 0; x < ECHOGRAM_RASTER_WIDTH; x += 1) {
    const columnTime = time - ECHOGRAM_WINDOW_SECONDS + (x / (ECHOGRAM_RASTER_WIDTH - 1)) * ECHOGRAM_WINDOW_SECONDS;
    const bottomDepth = echogramBottomDepth(columnTime);

    for (let y = 0; y < ECHOGRAM_RASTER_HEIGHT; y += 1) {
      const depth = (y / (ECHOGRAM_RASTER_HEIGHT - 1)) * ECHOGRAM_MAX_DEPTH_M;
      const quantizedTime = Math.floor(columnTime * 18);
      const quantizedDepth = Math.floor(depth * 125);
      const speckle = hashNoise(quantizedTime, quantizedDepth);
      const fineSpeckle = hashNoise(quantizedTime * 1.7, quantizedDepth * 1.31);
      const surfaceEcho = Math.exp(-depth * 28) * (0.18 + speckle * 0.08);
      const thermoclineDepth = 0.84 + Math.sin(columnTime * 0.08) * 0.05;
      const thermocline = Math.exp(-((depth - thermoclineDepth) ** 2) / (2 * 0.036 ** 2)) * 0.07;
      const bottomCore = Math.exp(-((depth - bottomDepth) ** 2) / (2 * 0.028 ** 2));
      const bottomHalo = depth > bottomDepth
        ? Math.exp(-(depth - bottomDepth) * 12) * 0.34
        : Math.exp(-(bottomDepth - depth) * 18) * 0.12;
      const waterColumn = 0.018 + speckle * 0.035 + fineSpeckle * 0.012;
      let signal = waterColumn + surfaceEcho + thermocline + bottomCore * (0.76 + speckle * 0.22) + bottomHalo;

      for (let i = 0; i < echoes.length; i += 1) {
        const echo = echoes[i];
        const dt = columnTime - echo.time;
        if (Math.abs(dt) > echo.timeSigma * 3.4) continue;

        const dd = depth - echo.depth;
        if (Math.abs(dd) > echo.depthSigma * 3.8) continue;

        const body = Math.exp(-((dt ** 2) / (2 * echo.timeSigma ** 2) + (dd ** 2) / (2 * echo.depthSigma ** 2)));
        const core = Math.exp(-((dt ** 2) / (2 * (echo.timeSigma * 0.42) ** 2) + (dd ** 2) / (2 * (echo.depthSigma * 0.48) ** 2)));
        signal += echo.strength * (body * 0.58 + core * 0.42);
      }

      if (depth > bottomDepth + 0.12) {
        signal *= 0.16;
      }

      const value = Math.pow(clamp(signal, 0, 1), 0.82);
      const [red, green, blue] = interpolateEkColor(value);
      const index = (y * ECHOGRAM_RASTER_WIDTH + x) * 4;
      data[index] = red;
      data[index + 1] = green;
      data[index + 2] = blue;
      data[index + 3] = 255;
    }
  }

  bufferCtx.putImageData(image, 0, 0);
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(buffer, plotX, plotY, plotW, plotH);

  ctx.save();
  ctx.strokeStyle = 'rgba(148, 163, 184, 0.26)';
  ctx.lineWidth = Math.max(1, dpr);
  ctx.font = `${Math.round(10 * dpr)}px ui-monospace, SFMono-Regular, Menlo, monospace`;
  ctx.fillStyle = 'rgba(203, 213, 225, 0.78)';
  ctx.textBaseline = 'middle';

  [0.5, 1, 1.5, 2].forEach((depth) => {
    const y = plotY + (depth / ECHOGRAM_MAX_DEPTH_M) * plotH;
    ctx.beginPath();
    ctx.moveTo(plotX, y);
    ctx.lineTo(plotX + plotW, y);
    ctx.stroke();
    ctx.textAlign = 'right';
    ctx.fillText(`${depth.toFixed(1)}m`, plotX - 7 * dpr, y);
  });

  [-40, -30, -20, -10, 0].forEach((offset) => {
    const x = plotX + ((ECHOGRAM_WINDOW_SECONDS + offset) / ECHOGRAM_WINDOW_SECONDS) * plotW;
    ctx.beginPath();
    ctx.moveTo(x, plotY);
    ctx.lineTo(x, plotY + plotH);
    ctx.stroke();
    ctx.textAlign = 'center';
    ctx.fillText(offset === 0 ? 'agora' : `${offset}s`, x, plotY + plotH + 16 * dpr);
  });

  ctx.strokeStyle = 'rgba(226, 232, 240, 0.72)';
  ctx.lineWidth = Math.max(1, 1.25 * dpr);
  ctx.strokeRect(plotX, plotY, plotW, plotH);
  ctx.strokeStyle = 'rgba(125, 211, 252, 0.95)';
  ctx.beginPath();
  ctx.moveTo(plotX + plotW - 1, plotY);
  ctx.lineTo(plotX + plotW - 1, plotY + plotH);
  ctx.stroke();

  const barX = width - padRight + 18 * dpr;
  const barY = plotY;
  const barW = Math.max(8, Math.round(10 * dpr));
  const barH = plotH;
  for (let y = 0; y < barH; y += 1) {
    const value = 1 - y / Math.max(1, barH - 1);
    const [red, green, blue] = interpolateEkColor(value);
    ctx.fillStyle = `rgb(${red}, ${green}, ${blue})`;
    ctx.fillRect(barX, barY + y, barW, 1);
  }
  ctx.strokeStyle = 'rgba(226, 232, 240, 0.45)';
  ctx.strokeRect(barX, barY, barW, barH);
  ctx.textAlign = 'left';
  ctx.fillStyle = 'rgba(203, 213, 225, 0.74)';
  ctx.fillText('-20', barX + 15 * dpr, barY + 3 * dpr);
  ctx.fillText('-45', barX + 15 * dpr, barY + barH * 0.5);
  ctx.fillText('-75', barX + 15 * dpr, barY + barH - 3 * dpr);
  ctx.fillText('Sv', barX, barY - 8 * dpr);

  ctx.restore();
};

// Scrolling echogram — Y = depth, X = time; independent fish detection complements camera AI count.
function UltrasoundPanel({ time, cameraCount, demoMode = true }: { time: number; cameraCount: number; demoMode?: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const latestStateRef = useRef({ time, sonarCount: cameraCount });

  const usCount = useMemo(() => {
    const cycle = Math.sin(time / 15.7) * 3.2 + Math.cos(time / 8.4) * 1.8;
    return Math.max(13, Math.min(32, Math.round(22 + cycle)));
  }, [time]);

  useEffect(() => {
    latestStateRef.current = { time, sonarCount: usCount };
    if (canvasRef.current) {
      renderEchogram(canvasRef.current, time, usCount);
    }
  }, [time, usCount]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const redraw = () => {
      const latest = latestStateRef.current;
      renderEchogram(canvas, latest.time, latest.sonarCount);
    };
    const resizeObserver = new ResizeObserver(redraw);
    resizeObserver.observe(canvas);
    redraw();

    return () => resizeObserver.disconnect();
  }, []);

  const diff = usCount - cameraCount;

  return (
    <div data-demo-tour="ultrasound" className="overflow-hidden rounded-lg border border-border bg-card shadow-sm dark:bg-[#02060b]">
      <div className="flex items-center justify-between border-b border-border px-3 py-2 dark:border-white/8">
        <div className="flex items-center gap-2">
          <Waves className="h-3.5 w-3.5 text-cyan-600 dark:text-cyan-300" />
          <span className="text-xs font-semibold text-cyan-700 dark:text-cyan-200">Echogram US · T-04</span>
          {!demoMode ? (
            <span className="rounded-full border border-amber-300/60 bg-amber-400/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-700 dark:border-amber-300/40 dark:bg-amber-400/10 dark:text-amber-300">
              Simulação técnica
            </span>
          ) : null}
        </div>
        <span className="hidden text-[10px] font-mono text-muted-foreground dark:text-slate-500 sm:inline">EK-style · 200 kHz</span>
      </div>

      <div className="h-[250px] min-h-[230px]">
        <canvas
          ref={canvasRef}
          className="block h-full w-full"
          aria-label="Echogram de sonar com profundidade no eixo vertical, tempo no eixo horizontal, ecos de peixe e retorno de fundo"
          role="img"
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-2.5 dark:border-white/8">
        <div className="flex flex-wrap items-center gap-4">
          <div>
            <p className="text-[10px] font-medium text-muted-foreground dark:text-slate-500">US detecta</p>
            <p className="text-xl font-bold leading-none text-cyan-700 dark:text-cyan-200">{usCount}</p>
          </div>
          <div className="h-6 w-px bg-border dark:bg-slate-700" />
          <div>
            <p className="text-[10px] font-medium text-muted-foreground dark:text-slate-500">IA câmara</p>
            <p className="text-xl font-bold leading-none text-muted-foreground dark:text-slate-400">{cameraCount}</p>
          </div>
        </div>
        {Math.abs(diff) >= 2 ? (
          <div className="flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-400" />
            <span className="text-xs font-semibold">
              Sugestão: ajustar para {usCount}
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1 text-emerald-800 dark:border-emerald-500/35 dark:bg-emerald-500/10 dark:text-emerald-300">
            <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-400" />
            <span className="text-xs font-semibold">Contagens concordam</span>
          </div>
        )}
      </div>
    </div>
  );
}

export function VisionDemo({
  onBack,
  onOpenAlerts,
  initialAlertId = null,
  demoMode = true,
  embedded = false,
  clientId = null,
  projectId = null,
  zoneId = null
}: VisionDemoProps) {
  const liveMode = !demoMode;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const liveImageRef = useRef<HTMLImageElement | null>(null);
  const localCameraVideoRef = useRef<HTMLVideoElement | null>(null);
  const localCameraStreamRef = useRef<MediaStream | null>(null);
  const inferenceRunningRef = useRef(false);
  const initialSeekDoneRef = useRef(false);
  const [time, setTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [injection, setInjection] = useState<Injection | null>(null);
  const [injectionEndsAt, setInjectionEndsAt] = useState<number | null>(null);
  const [assistantOpen, setAssistantOpen] = useState(false);

  // Live data (production mode only)
  const [liveZone, setLiveZone] = useState<LiveZoneContext | null>(null);
  const [liveFeeds, setLiveFeeds] = useState<LiveCameraFeed[]>([]);
  const [liveFeed, setLiveFeed] = useState<LiveCameraFeed | null>(null);
  const [selectedLiveFeedKey, setSelectedLiveFeedKey] = useState<string | null>(null);
  const [liveFeedViewMode, setLiveFeedViewMode] = useState<'grid' | 'focus'>('grid');
  const [liveFeedTick, setLiveFeedTick] = useState(Date.now());
  const [liveFeedDisplayUrl, setLiveFeedDisplayUrl] = useState<string | null>(null);
  const [liveFeedFrameLoadedAt, setLiveFeedFrameLoadedAt] = useState<number | null>(null);
  const [liveFeedBuffering, setLiveFeedBuffering] = useState(false);
  const [liveFeedPrimed, setLiveFeedPrimed] = useState(false);
  const [liveFeedFailed, setLiveFeedFailed] = useState(false);
  const [liveCapture, setLiveCapture] = useState<LiveCapture | null>(null);
  const [liveSensors, setLiveSensors] = useState<LiveSensorReading | null>(null);
  const [liveDevicePresence, setLiveDevicePresence] = useState<LiveDevicePresence | null>(null);
  const [liveRealtimeOnline, setLiveRealtimeOnline] = useState<boolean | null>(null);
  const [liveRealtimePresenceSeen, setLiveRealtimePresenceSeen] = useState(false);
  const [liveEdgeAgentVersion, setLiveEdgeAgentVersion] = useState<string | null>(null);
  const [liveDetectionCount, setLiveDetectionCount] = useState<number | null>(null);
  const [browserInference, setBrowserInference] = useState<BrowserInferenceResult | null>(null);
  const [browserInferenceError, setBrowserInferenceError] = useState<string | null>(null);
  const [edgeInference, setEdgeInference] = useState<LatestEdgeInference | null>(null);
  const [localCameraStream, setLocalCameraStream] = useState<MediaStream | null>(null);
  const [localCameraStarting, setLocalCameraStarting] = useState(false);
  const [localCameraError, setLocalCameraError] = useState<string | null>(null);
  const [localVideoInputs, setLocalVideoInputs] = useState<MediaDeviceInfo[]>([]);
  const [selectedLocalCameraId, setSelectedLocalCameraId] = useState('');
  const [liveUltrasoundAvailable, setLiveUltrasoundAvailable] = useState<boolean | null>(demoMode ? true : null);
  const [trainingBurst, setTrainingBurst] = useState<LiveTrainingBurstState | null>(null);
  const [trainingBurstBusy, setTrainingBurstBusy] = useState(false);
  const [trainingExportPrompt, setTrainingExportPrompt] = useState<TrainingExportPrompt | null>(null);
  const [trainingExportAction, setTrainingExportAction] = useState<TrainingExportAction | null>(null);
  const [trainingExportProgress, setTrainingExportProgress] = useState<{ completed: number; total: number } | null>(null);
  const [liveError, setLiveError] = useState<string | null>(null);
  const [liveRefreshKey, setLiveRefreshKey] = useState(0);
  const liveFeedBufferCountRef = useRef(0);
  const liveFeedDisplayUrlRef = useRef<string | null>(null);
  const trainingExportPromptKeyRef = useRef<string | null>(null);
  const activeLiveFeedKey = liveFeed ? liveFeedKey(liveFeed) : null;
  const liveNowMs = liveFeedTick;
  const liveCameraOnline = liveMode && liveFeed ? cameraFeedOnline(liveFeed, liveNowMs, liveFeedFrameLoadedAt) : null;
  const effectiveCameraOnline = localCameraStream ? true : liveCameraOnline;
  const liveMountedAtRef = useRef<number>(Date.now());

  function openTrainingExportPromptFor(state: LiveTrainingBurstState | null, feed = liveFeed) {
    if (!state || state.active || state.count <= 0 || !feed) return;
    const key = [
      liveFeedKey(feed),
      state.startedAt ?? '',
      state.completedAt ?? '',
      state.count
    ].join(':');
    if (trainingExportPromptKeyRef.current === key) return;

    trainingExportPromptKeyRef.current = key;
    setTrainingExportAction(null);
    setTrainingExportProgress(null);
    setTrainingExportPrompt({
      cameraId: feed.cameraId,
      cameraCode: feed.cameraCode,
      cameraName: feed.cameraName,
      folderPath: state.folderPath,
      count: state.count,
      target: state.target,
      completedAt: state.completedAt
    });
  }

  const loadTrainingDatasetForPrompt = async (prompt: TrainingExportPrompt) => {
    const dataset = await fetchCameraTrainingDataset({
      cameraId: prompt.cameraId,
      cameraCode: prompt.cameraCode
    });
    if (!dataset.images.length) {
      throw new Error('Esta recolha ainda não tem imagens disponíveis para exportar.');
    }
    return dataset;
  };

  const buildTrainingZip = async (dataset: LiveTrainingDatasetExport) => {
    setTrainingExportProgress({ completed: 0, total: dataset.images.length });
    return await fetchRemoteFilesAsZip(
      dataset.images.map((image) => ({
        name: image.name,
        url: image.url,
        lastModified: image.updatedAt ?? image.createdAt
      })),
      (completed, total) => setTrainingExportProgress({ completed, total })
    );
  };

  const shareFilesSupported = (fileName: string) => {
    if (!navigator.share || !navigator.canShare || typeof File === 'undefined') return false;
    const testFile = new File([new Uint8Array([0])], fileName, { type: 'application/zip' });
    try {
      return navigator.canShare({ files: [testFile] });
    } catch {
      return false;
    }
  };

  const handleDownloadTrainingDataset = async () => {
    if (!trainingExportPrompt || trainingExportAction) return;
    const prompt = trainingExportPrompt;
    const fileName = trainingArchiveFileName(prompt);
    setTrainingExportAction('download');

    try {
      const dataset = await loadTrainingDatasetForPrompt(prompt);
      const zip = await buildTrainingZip(dataset);
      downloadBlob(zip, fileName);
      toast.success(`ZIP descarregado com ${dataset.images.length} imagens.`);
      setTrainingExportPrompt(null);
    } catch (error: any) {
      toast.error(error?.message ?? 'Não foi possível descarregar a recolha.');
    } finally {
      setTrainingExportAction(null);
      setTrainingExportProgress(null);
    }
  };

  const handleShareTrainingDataset = async () => {
    if (!trainingExportPrompt || trainingExportAction) return;
    const prompt = trainingExportPrompt;
    const fileName = trainingArchiveFileName(prompt);
    setTrainingExportAction('share');

    try {
      const dataset = await loadTrainingDatasetForPrompt(prompt);
      const shareText = buildTrainingShareText(dataset, prompt);
      const title = 'AQL Vision - recolha de treino';

      if (shareFilesSupported(fileName)) {
        try {
          const zip = await buildTrainingZip(dataset);
          const file = new File([zip], fileName, { type: 'application/zip' });
          await navigator.share({ title, text: shareText, files: [file] });
          toast.success('Recolha enviada para partilha.');
          setTrainingExportPrompt(null);
          return;
        } catch (error: any) {
          if (error?.name === 'AbortError') return;
          console.warn('AQL Vision file share failed, falling back to links:', error);
        }
      }

      if (navigator.share) {
        try {
          await navigator.share({ title, text: shareText });
          toast.success('Partilha aberta com os links da recolha.');
          setTrainingExportPrompt(null);
          return;
        } catch (error: any) {
          if (error?.name === 'AbortError') return;
        }
      }

      await copyTextToClipboard(shareText);
      toast.success('Links copiados para a área de transferência.');
      setTrainingExportPrompt(null);
    } catch (error: any) {
      toast.error(error?.message ?? 'Não foi possível preparar a partilha.');
    } finally {
      setTrainingExportAction(null);
      setTrainingExportProgress(null);
    }
  };

  useEffect(() => {
    if (liveMode) return;
    const interval = window.setInterval(() => {
      const video = videoRef.current;
      if (video) {
        setTime(video.currentTime || 0);
        setPlaying(!video.paused && !video.ended);
      }
      if (injectionEndsAt && Date.now() > injectionEndsAt) {
        setInjection(null);
        setInjectionEndsAt(null);
      }
    }, 220);

    return () => window.clearInterval(interval);
  }, [injectionEndsAt, liveMode]);

  // Live mode: keep `time` ticking for animations (echogram canvas + overlays).
  useEffect(() => {
    if (!liveMode) return;
    liveMountedAtRef.current = Date.now();
    const interval = window.setInterval(() => {
      setTime((Date.now() - liveMountedAtRef.current) / 1000);
    }, 250);
    return () => window.clearInterval(interval);
  }, [liveMode]);

  // Live mode: resolve the active zone from props.
  useEffect(() => {
    if (!liveMode) {
      setLiveZone(null);
      setLiveFeeds([]);
      setLiveFeed(null);
      setLiveFeedDisplayUrl(null);
      setLiveFeedBuffering(false);
      setLiveFeedPrimed(false);
      setLiveFeedViewMode('grid');
      liveFeedBufferCountRef.current = 0;
      return;
    }
    let active = true;
    (async () => {
      try {
        const zone = await resolveActiveZone({
          clientId: clientId ?? undefined,
          projectId: projectId ?? undefined,
          zoneId: zoneId ?? undefined
        });
        if (active) setLiveZone(zone);
      } catch (err) {
        if (!active) return;
        setLiveError(err instanceof Error ? err.message : 'Zone resolution failed.');
      }
    })();
    return () => {
      active = false;
    };
  }, [clientId, projectId, zoneId, liveMode]);

  useEffect(() => {
    if (liveMode) setLiveFeedViewMode('grid');
  }, [clientId, projectId, zoneId, liveMode]);

  useEffect(() => {
    if (!liveMode) {
      setLiveUltrasoundAvailable(true);
      return;
    }

    const resolvedZoneId = liveZone?.zoneId ?? zoneId ?? undefined;
    if (!resolvedZoneId && !clientId) {
      setLiveUltrasoundAvailable(false);
      return;
    }

    let active = true;
    setLiveUltrasoundAvailable(null);

    (async () => {
      const available = await hasActiveUltrasoundEquipment({
        zoneId: resolvedZoneId,
        clientId: clientId ?? undefined,
        projectId: projectId ?? undefined
      });
      if (active) setLiveUltrasoundAvailable(available);
    })();

    return () => {
      active = false;
    };
  }, [liveMode, liveZone?.zoneId, zoneId, clientId, projectId, liveRefreshKey]);

  useEffect(() => {
    if (!liveMode || !liveDevicePresence?.kitId) {
      setLiveRealtimeOnline(null);
      setLiveRealtimePresenceSeen(false);
      setLiveEdgeAgentVersion(null);
      return;
    }

    const kitId = liveDevicePresence.kitId;
    const channel = supabase.channel(`aql-edge-${kitId}`);
    const syncPresence = () => {
      const state = channel.presenceState() as Record<string, Array<Record<string, unknown>>>;
      const edgePresence = Object.values(state).flat().find((presence) =>
        presence.agent === 'aql-vision-raspberry' && String(presence.kit_id ?? '') === kitId
      );
      const edgeOnline = Boolean(edgePresence);
      if (edgeOnline) setLiveRealtimePresenceSeen(true);
      setLiveEdgeAgentVersion(edgePresence?.agent_version ? String(edgePresence.agent_version) : null);
      setLiveRealtimeOnline(edgeOnline);
    };

    channel
      .on('presence', { event: 'sync' }, syncPresence)
      .on('presence', { event: 'join' }, syncPresence)
      .on('presence', { event: 'leave' }, syncPresence)
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          setLiveRealtimeOnline(null);
        }
      });

    return () => {
      setLiveRealtimeOnline(null);
      setLiveRealtimePresenceSeen(false);
      setLiveEdgeAgentVersion(null);
      void supabase.removeChannel(channel);
    };
  }, [liveDevicePresence?.kitId, liveMode]);

  useEffect(() => {
    liveFeedDisplayUrlRef.current = liveFeedDisplayUrl;
  }, [liveFeedDisplayUrl]);

  // Live mode: resolve the high-frequency camera feed once per scope. The edge
  // device overwrites the same Storage object, so the image itself is refreshed
  // separately with a cache-busting query string.
  useEffect(() => {
    if (!liveMode) return;
    const resolvedZoneId = liveZone?.zoneId ?? zoneId ?? undefined;
    if (!resolvedZoneId && !clientId) return;

    let active = true;
    const loadFeeds = async () => {
      try {
        const feeds = await fetchLiveCameraFeeds({
          zoneId: resolvedZoneId,
          clientId: clientId ?? undefined,
          projectId: projectId ?? undefined
        });
        if (!active) return;
        setLiveFeeds(feeds);
        const preferred = feeds.find((feed) => liveFeedKey(feed) === selectedLiveFeedKey)
          ?? feeds.find((feed) => feed.isPrimary)
          ?? feeds[0]
          ?? null;
        setLiveFeed(preferred);
        setSelectedLiveFeedKey(preferred ? liveFeedKey(preferred) : null);
        setLiveFeedFailed(false);
        setLiveFeedTick(Date.now());
      } catch (err) {
        if (!active) return;
        setLiveFeed(null);
        setLiveFeedFailed(true);
        setLiveError(err instanceof Error ? err.message : 'Camera feed resolution failed.');
      }
    };

    void loadFeeds();
    const timer = window.setInterval(loadFeeds, LIVE_SENSORS_REFRESH_MS);

    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [liveMode, liveZone?.zoneId, zoneId, clientId, projectId, liveRefreshKey, selectedLiveFeedKey]);

  useEffect(() => {
    if (!liveMode || !liveFeed) {
      setLiveFeedDisplayUrl(null);
      setLiveFeedBuffering(false);
      setLiveFeedPrimed(false);
      setLiveFeedFrameLoadedAt(null);
      liveFeedBufferCountRef.current = 0;
      return;
    }

    setTrainingBurst(liveFeed.training ?? null);
    liveFeedBufferCountRef.current = 0;
    liveFeedDisplayUrlRef.current = null;
    setLiveFeedDisplayUrl(null);
    setLiveFeedFrameLoadedAt(null);
    setLiveFeedPrimed(false);
    setLiveFeedBuffering(true);
    setLiveFeedFailed(false);
    setLiveFeedTick(Date.now());
  }, [activeLiveFeedKey, liveFeed?.url, liveMode]);

  useEffect(() => {
    localCameraStreamRef.current = localCameraStream;
    const video = localCameraVideoRef.current;
    if (video && localCameraStream && video.srcObject !== localCameraStream) {
      video.srcObject = localCameraStream;
      void video.play().catch(() => undefined);
    }
  }, [localCameraStream, liveFeedViewMode]);

  useEffect(() => {
    if (!localCameraStream) return;
    liveFeedBufferCountRef.current = LIVE_FEED_BUFFER_FRAMES;
    setLiveFeedBuffering(false);
    setLiveFeedPrimed(true);
  }, [localCameraStream]);

  useEffect(() => {
    if (!localCameraStream || typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) return;
    let active = true;
    const refreshDevices = async () => {
      const devices = await navigator.mediaDevices.enumerateDevices().catch(() => []);
      if (active) setLocalVideoInputs(devices.filter((device) => device.kind === 'videoinput'));
    };
    void refreshDevices();
    navigator.mediaDevices.addEventListener?.('devicechange', refreshDevices);
    return () => {
      active = false;
      navigator.mediaDevices.removeEventListener?.('devicechange', refreshDevices);
    };
  }, [localCameraStream]);

  useEffect(() => () => {
    localCameraStreamRef.current?.getTracks().forEach((track) => track.stop());
    localCameraStreamRef.current = null;
  }, []);

  useEffect(() => {
    if (localCameraStream) return;
    const hasRemoteFrame = Boolean(liveFeedDisplayUrl);
    if (!liveMode || liveFeed?.kitId || liveFeed?.inference.mode !== 'browser-onnx' || !liveFeed.inference.modelUrl || !hasRemoteFrame) {
      setBrowserInference(null);
      setBrowserInferenceError(null);
      return;
    }
    const image = liveImageRef.current;
    if (!image || (image instanceof HTMLImageElement && (!image.complete || !image.naturalWidth)) || inferenceRunningRef.current) return;
    let active = true;
    inferenceRunningRef.current = true;
    runBrowserOnnxInference({
      modelUrl: liveFeed.inference.modelUrl,
      image,
      labels: liveFeed.inference.labels,
      inputSize: liveFeed.inference.inputSize,
      confidenceThreshold: liveFeed.inference.confidenceThreshold
    }).then((result) => {
      if (!active) return;
      setBrowserInference(result);
      setBrowserInferenceError(null);
      setLiveDetectionCount(result.detections.length);
    }).catch((error) => {
      if (!active) return;
      setBrowserInferenceError(error instanceof Error ? error.message : 'Inferência ONNX indisponível.');
    }).finally(() => { inferenceRunningRef.current = false; });
    return () => { active = false; };
  }, [liveMode, liveFeedDisplayUrl, liveFeed?.kitId, liveFeed?.inference.modelUrl, liveFeed?.inference.mode, localCameraStream]);

  useEffect(() => {
    if (!localCameraStream || !liveMode || liveFeed?.inference.mode !== 'browser-onnx' || !liveFeed.inference.modelUrl) return;

    let active = true;
    let timer: number | null = null;
    const runLocalInference = async () => {
      if (!active) return;
      const video = localCameraVideoRef.current;
      if (!video || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA || inferenceRunningRef.current) {
        timer = window.setTimeout(runLocalInference, 250);
        return;
      }

      inferenceRunningRef.current = true;
      try {
        const result = await runBrowserOnnxInference({
          modelUrl: liveFeed.inference.modelUrl!,
          image: video,
          labels: liveFeed.inference.labels,
          inputSize: liveFeed.inference.inputSize,
          confidenceThreshold: liveFeed.inference.confidenceThreshold
        });
        if (!active) return;
        setBrowserInference(result);
        setBrowserInferenceError(null);
        setLiveDetectionCount(result.detections.length);
      } catch (error) {
        if (active) setBrowserInferenceError(error instanceof Error ? error.message : 'Inferência ONNX indisponível.');
      } finally {
        inferenceRunningRef.current = false;
        if (active) timer = window.setTimeout(runLocalInference, 60);
      }
    };

    timer = window.setTimeout(runLocalInference, 150);
    return () => {
      active = false;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [localCameraStream, liveMode, liveFeed?.inference.modelUrl, liveFeed?.inference.mode]);

  const startLocalCamera = async (deviceId?: string) => {
    if (localCameraStarting || (localCameraStream && !deviceId)) return;
    try {
      setLocalCameraStarting(true);
      setLocalCameraError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: deviceId
          ? { deviceId: { exact: deviceId } }
          : { facingMode: { ideal: 'environment' } },
        audio: false
      });
      const previousStream = localCameraStreamRef.current;
      const activeTrack = stream.getVideoTracks()[0];
      const resolvedDeviceId = activeTrack?.getSettings().deviceId ?? deviceId ?? '';
      const video = localCameraVideoRef.current;
      if (video) {
        video.srcObject = stream;
        await video.play().catch(() => undefined);
      }
      localCameraStreamRef.current = stream;
      setLocalCameraStream(stream);
      setSelectedLocalCameraId(resolvedDeviceId);
      const devices = await navigator.mediaDevices.enumerateDevices().catch(() => []);
      setLocalVideoInputs(devices.filter((item) => item.kind === 'videoinput'));
      previousStream?.getTracks().forEach((track) => track.stop());
      setLiveFeedFailed(false);
      toast.success(deviceId ? 'Câmara alterada.' : 'Câmara deste dispositivo ligada.');
    } catch (error: any) {
      const message = error?.name === 'NotAllowedError'
        ? 'Autoriza o acesso à câmara nas permissões do browser.'
        : error?.message ?? 'Não foi possível abrir a câmara deste dispositivo.';
      setLocalCameraError(message);
      toast.error(message);
    } finally {
      setLocalCameraStarting(false);
    }
  };

  const stopLocalCamera = () => {
    localCameraStreamRef.current?.getTracks().forEach((track) => track.stop());
    localCameraStreamRef.current = null;
    setLocalCameraStream(null);
    setLocalVideoInputs([]);
    setSelectedLocalCameraId('');
    setBrowserInference(null);
  };

  useEffect(() => {
    if (!liveMode || !liveFeed) {
      setTrainingBurst(null);
      return;
    }

    setTrainingBurst(liveFeed.training ?? null);

    const load = async () => {
      const state = await fetchCameraTrainingBurstState({
        cameraId: liveFeed.cameraId,
        cameraCode: liveFeed.cameraCode
      }).catch(() => null);
      if (state) setTrainingBurst(state);
    };

    void load();
    const interval = window.setInterval(load, 2000);
    return () => window.clearInterval(interval);
  }, [liveMode, activeLiveFeedKey, liveFeed?.cameraId, liveFeed?.cameraCode]);

  useEffect(() => {
    if (!liveMode || !liveFeed) return;
    const interval = window.setInterval(() => {
      setLiveFeedTick(Date.now());
    }, LIVE_FEED_FRAME_REFRESH_MS);
    return () => window.clearInterval(interval);
  }, [liveMode, liveFeed?.url]);

  useEffect(() => {
    const kitId = liveFeed?.kitId;
    const cameraId = liveFeed?.cameraId;
    if (!liveMode || !kitId || !cameraId) {
      setEdgeInference(null);
      return;
    }

    let active = true;
    const load = async () => {
      try {
        const payload = await loadLatestEdgeInference(kitId, cameraId);
        if (!active) return;
        const inference = payload.latest_inference;
        const capturedAt = timestampMs(inference?.captured_at);
        const fresh = capturedAt !== null && Date.now() - capturedAt >= 0 && Date.now() - capturedAt <= LIVE_INFERENCE_STALE_MS;
        setEdgeInference(fresh ? inference : null);
        setLiveDetectionCount(fresh ? inference?.detections.length ?? 0 : 0);
      } catch {
        if (active) setEdgeInference(null);
      }
    };

    void load();
    const interval = window.setInterval(load, 1000);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [liveMode, liveFeed?.kitId, liveFeed?.cameraId]);

  useEffect(() => {
    if (!liveMode || !liveFeedFailed || !liveFeed) return;
    const retry = window.setTimeout(() => {
      setLiveFeedFailed(false);
      setLiveFeedTick(Date.now());
    }, 15_000);
    return () => window.clearTimeout(retry);
  }, [liveMode, liveFeedFailed, liveFeed?.url]);

  // Live mode: poll heartbeat, latest capture (30s) + sensor reading (5s).
  useEffect(() => {
    if (!liveMode) return;
    const resolvedZoneId = liveZone?.zoneId ?? zoneId ?? undefined;
    if (!resolvedZoneId && !clientId) return;

    let active = true;

    const loadCapture = async () => {
      try {
        const capture = await fetchLatestCapture({
          zoneId: resolvedZoneId,
          clientId: clientId ?? undefined,
          projectId: projectId ?? undefined
        });
        if (!active) return;
        setLiveCapture(capture);
        if (capture?.id) {
          const count = await fetchDetectionCount(capture.id);
          if (active) setLiveDetectionCount(count);
        } else if (active) {
          setLiveDetectionCount(null);
        }
      } catch (err) {
        if (!active) return;
        setLiveError(err instanceof Error ? err.message : 'Capture fetch failed.');
      }
    };

    const loadSensors = async () => {
      try {
        const reading = await fetchLatestSensorReading({
          zoneId: resolvedZoneId,
          clientId: clientId ?? undefined,
          projectId: projectId ?? undefined
        });
        if (active) setLiveSensors(reading);
      } catch (err) {
        if (!active) return;
        setLiveError(err instanceof Error ? err.message : 'Sensor fetch failed.');
      }
    };

    const loadDevicePresence = async () => {
      try {
        const presence = await fetchLiveDevicePresence({
          zoneId: resolvedZoneId,
          clientId: clientId ?? undefined,
          projectId: projectId ?? undefined
        });
        if (active) setLiveDevicePresence(presence);
      } catch (err) {
        if (!active) return;
        setLiveError(err instanceof Error ? err.message : 'Device heartbeat fetch failed.');
      }
    };

    void loadCapture();
    void loadSensors();
    void loadDevicePresence();
    const captureTimer = window.setInterval(loadCapture, LIVE_CAPTURE_REFRESH_MS);
    const sensorTimer = window.setInterval(loadSensors, LIVE_SENSORS_REFRESH_MS);
    const presenceTimer = window.setInterval(loadDevicePresence, LIVE_SENSORS_REFRESH_MS);

    return () => {
      active = false;
      window.clearInterval(captureTimer);
      window.clearInterval(sensorTimer);
      window.clearInterval(presenceTimer);
    };
  }, [liveMode, liveZone?.zoneId, zoneId, clientId, projectId, liveRefreshKey]);

  const visualEvent = useMemo(
    () => (liveMode ? null : VISUAL_EVENTS.find((event) => time >= event.start && time <= event.end) ?? null),
    [time, liveMode]
  );

  const liveFeedTargetUrl = useMemo(
    () => (liveMode && liveFeed && !liveFeedFailed && !localCameraStream ? withCacheBuster(liveFeed.url, liveFeedTick) : null),
    [liveMode, liveFeed, liveFeedFailed, liveFeedTick, localCameraStream]
  );
  const liveFeedFrameUrl = liveFeedDisplayUrl;

  useEffect(() => {
    if (!liveMode || !liveFeedTargetUrl || typeof window === 'undefined') return;

    let cancelled = false;
    const image = new window.Image();
    if (liveFeedBufferCountRef.current < LIVE_FEED_BUFFER_FRAMES) {
      setLiveFeedBuffering(true);
    }

    image.onload = () => {
      if (cancelled) return;
      const loadedAt = Date.now();
      liveFeedBufferCountRef.current = Math.min(LIVE_FEED_BUFFER_FRAMES, liveFeedBufferCountRef.current + 1);
      liveFeedDisplayUrlRef.current = liveFeedTargetUrl;
      setLiveFeedDisplayUrl(liveFeedTargetUrl);
      setLiveFeedFrameLoadedAt(loadedAt);
      setLiveFeedFailed(false);

      if (liveFeedBufferCountRef.current >= LIVE_FEED_BUFFER_FRAMES) {
        setLiveFeedPrimed(true);
        setLiveFeedBuffering(false);
      }
    };

    image.onerror = () => {
      if (cancelled) return;
      setLiveFeedBuffering(false);
      if (!liveFeedDisplayUrlRef.current) {
        setLiveFeedFailed(true);
      }
    };

    image.decoding = 'async';
    image.src = liveFeedTargetUrl;

    return () => {
      cancelled = true;
    };
  }, [liveFeedTargetUrl, liveMode]);

  const telemetry = useMemo(() => {
    if (liveMode) {
      // Use live sensor values when available; otherwise show "--" via NaN guard downstream.
      return {
        temperature: liveSensors?.temperature ?? NaN,
        ph: liveSensors?.ph ?? NaN,
        oxygen: liveSensors?.oxygen ?? NaN,
        salinity: liveSensors?.salinity ?? NaN
      };
    }
    return buildTelemetry(time, injection);
  }, [liveMode, liveSensors, injection, time]);

  const metricRows = useMemo(() => {
    return (Object.keys(telemetry) as MetricKey[]).map((key) => {
      const value = telemetry[key];
      return {
        key,
        value,
        level: metricLevel(key, value),
        ...METRIC_CONFIG[key]
      };
    });
  }, [telemetry]);

  const fishEstimate = useMemo(() => {
    if (liveMode) {
      const count = liveDetectionCount ?? 0;
      // Confidence isn't surfaced per-capture in production yet; show a neutral placeholder.
      return { count, confidence: liveDetectionCount === null ? 0 : 90 };
    }
    const schooling = Math.sin(time / 12.4) * 4 + Math.cos(time / 7.8) * 2;
    const micro = Math.sin(time / 2.9) * 1.2 + Math.cos(time / 1.7) * 0.8;
    const count = Math.max(14, Math.min(28, Math.round(20 + schooling + micro)));
    const confidence = Math.max(80, Math.min(96, Math.round(88 + Math.sin(time / 8.3) * 5)));
    return { count, confidence };
  }, [time, liveMode, liveDetectionCount]);

  const activeAlerts = useMemo<DemoAlert[]>(() => {
    const alerts: DemoAlert[] = [];
    const timestamp = formatClock(time);

    if (visualEvent) {
      alerts.push({
        id: visualEvent.id,
        title: visualEvent.title,
        detail: `Confiança visual ${visualEvent.confidence}% · validação humana pendente`,
        level: visualEvent.confidence >= 80 ? 'critical' : 'attention',
        timestamp
      });
    }

    metricRows.forEach((metric) => {
      if (metric.level === 'normal') return;
      const metricAlertId =
        metric.key === 'oxygen'
          ? 'demo-env-oxygen-drop'
          : metric.key === 'ph'
            ? 'demo-env-ph-drift'
            : 'demo-env-temperature-peak';
      alerts.push({
        id: metricAlertId,
        title: `${metric.label} fora do alvo`,
        detail: `${metric.value.toFixed(metric.decimals)}${metric.unit ? ` ${metric.unit}` : ''} · ${metric.target}`,
        level: metric.level,
        timestamp
      });
    });

    if (injection === 'sensor') {
      alerts.push({
        id: 'demo-sensor-calibration',
        title: 'Sensor O2 sem calibração',
        detail: 'Leitura ambiental marcada para verificação',
        level: 'attention',
        timestamp
      });
    }

    return alerts;
  }, [injection, metricRows, time, visualEvent]);

  const overallLevel: AlertLevel = activeAlerts.some((alert) => alert.level === 'critical')
    ? 'critical'
    : activeAlerts.length
      ? 'attention'
      : 'normal';

  const liveAnyCameraOnline = liveMode ? liveFeeds.some((feed) => cameraFeedOnline(feed, liveNowMs)) : null;
  const liveLastCommunicationAt = useMemo(() => {
    if (!liveMode) return null;
    const timestamps = [
      timestampMs(liveDevicePresence?.lastHeartbeatAt),
      ...liveFeeds.map((feed) => timestampMs(feed.lastFrameAt)),
      timestampMs(liveCapture?.captured_at),
      timestampMs(liveSensors?.recorded_at)
    ].filter((value): value is number => value !== null);
    if (!timestamps.length) return null;
    return new Date(Math.max(...timestamps)).toISOString();
  }, [liveCapture?.captured_at, liveDevicePresence?.lastHeartbeatAt, liveFeeds, liveMode, liveSensors?.recorded_at]);
  const durableDeviceOnline = isHeartbeatOnline(
    liveDevicePresence?.lastHeartbeatAt,
    liveDevicePresence?.heartbeatIntervalSeconds ?? 120,
    liveNowMs
  ) ?? liveDevicePresence?.online ?? Boolean(liveAnyCameraOnline);
  const liveDeviceOnline = liveMode
    ? liveRealtimePresenceSeen
      ? liveRealtimeOnline ?? durableDeviceOnline
      : durableDeviceOnline
    : null;
  const lastMetadataSync = liveMode
    ? formatTimeWithMilliseconds(liveLastCommunicationAt) ?? '--'
    : new Date(Date.now() - (injection === 'sensor' ? 42 : Math.max(1, Math.round((time % 6) + 1))) * 1000).toLocaleTimeString('pt-PT', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  const lastFrameReceived = liveMode && liveFeedFrameLoadedAt
    ? formatTimeWithMilliseconds(new Date(liveFeedFrameLoadedAt).toISOString()) ?? lastMetadataSync
    : lastMetadataSync;
  const deviceLastContact = liveMode
    ? formatTimeWithMilliseconds(liveDevicePresence?.lastHeartbeatAt) ?? lastMetadataSync
    : lastMetadataSync;
  const sensorsOk = injection !== 'sensor' && !metricRows.some((metric) => metric.level === 'critical');
  const activeCriticalAlertCount = activeAlerts.filter((alert) => alert.level === 'critical').length;
  const assistantSummary = useMemo<AqlAssistantLocalSummary>(() => ({
    activeAlerts: activeAlerts.length,
    activeCriticalAlerts: activeCriticalAlertCount,
    sensorOutOfRangeCount: metricRows.filter((metric) => metric.level !== 'normal').length,
    staleSensorCount: injection === 'sensor' ? 1 : 0,
    deviceOfflineCount: liveDeviceOnline === false ? 1 : 0,
    lowMeasurementConfidence: fishEstimate.confidence < 85,
    recentPathologyCases: activeAlerts.filter((alert) => alert.id.startsWith('demo-visual')).length
  }), [activeAlerts, activeCriticalAlertCount, fishEstimate.confidence, injection, liveDeviceOnline, metricRows]);
  const assistantContextValue = useMemo(() => ({
    client_id: null,
    current_page: 'camera_live' as const,
    time_window: '24h' as const,
    client_name: 'AQL Demo Aquaculture',
    species_label: 'Pregado',
    phase_label: 'adult',
    location_label: 'Unidade Piloto',
    zone_label: 'T-04',
    kit_label: 'KIT-04',
    camera_label: 'CAM-04'
  }), []);
  const initialSeekTime = useMemo(() => getDemoDetectionTime(initialAlertId), [initialAlertId]);

  const jumpTo = (seconds: number, autoPlay = true) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = seconds;
    setTime(seconds);
    if (autoPlay) {
      video.play().catch(() => undefined);
    }
  };

  const seekToInitialDetection = (video = videoRef.current) => {
    if (initialSeekDoneRef.current || initialSeekTime === null || !video) return;
    initialSeekDoneRef.current = true;
    video.currentTime = initialSeekTime;
    setTime(initialSeekTime);
  };

  useEffect(() => {
    initialSeekDoneRef.current = false;
  }, [initialAlertId]);

  useEffect(() => {
    const timer = window.setTimeout(() => seekToInitialDetection(), 350);
    return () => window.clearTimeout(timer);
  }, [initialSeekTime]);

  const togglePlayback = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play().catch(() => undefined);
    } else {
      video.pause();
    }
  };

  const resetDemo = () => {
    setInjection(null);
    setInjectionEndsAt(null);
    jumpTo(0, false);
    videoRef.current?.pause();
  };

  const jumpToNextDetection = () => {
    const next = DEMO_DETECTION_TIMELINE.find((event) => event.time > time + 1) ?? DEMO_DETECTION_TIMELINE[0];
    jumpTo(next.time);
  };

  const jumpToPreviousDetection = () => {
    const previous = [...DEMO_DETECTION_TIMELINE].reverse().find((event) => event.time < time - 1) ?? DEMO_DETECTION_TIMELINE[DEMO_DETECTION_TIMELINE.length - 1];
    jumpTo(previous.time);
  };

  const inject = (next: Injection) => {
    setInjection(next);
    setInjectionEndsAt(Date.now() + 11000);
  };

  const openLiveFeed = (feed: LiveCameraFeed) => {
    if (localCameraStream && liveFeedKey(feed) !== activeLiveFeedKey) stopLocalCamera();
    setLiveFeed(feed);
    setSelectedLiveFeedKey(liveFeedKey(feed));
    setTrainingBurst(feed.training ?? null);
    setLiveFeedFailed(false);
    setLiveFeedTick(Date.now());
    setLiveFeedViewMode('focus');
  };

  const toggleTrainingBurst = async () => {
    if (!liveFeed || trainingBurstBusy) return;
    if (!liveCameraOnline) {
      toast.warning('Câmara offline', { description: 'Não é possível pedir imagens enquanto não houver frames recentes.' });
      return;
    }

    try {
      setTrainingBurstBusy(true);
      const nextState = trainingBurst?.active
        ? await stopCameraTrainingBurst({ cameraId: liveFeed.cameraId, cameraCode: liveFeed.cameraCode })
        : await startCameraTrainingBurst({
          cameraId: liveFeed.cameraId,
          cameraCode: liveFeed.cameraCode,
          target: trainingBurst?.target ?? liveFeed.training?.target ?? 50
        });

      if (nextState) setTrainingBurst(nextState);
      toast.success(nextState?.active ? `Recolha iniciada: 0/${nextState.target}` : 'Recolha parada');
      if (trainingBurst?.active && nextState && !nextState.active) {
        openTrainingExportPromptFor(nextState);
      }
      setLiveRefreshKey((value) => value + 1);
    } catch (error: any) {
      toast.error(error?.message ?? 'Não foi possível atualizar o burst de treino.');
    } finally {
      setTrainingBurstBusy(false);
    }
  };

  const progress = Math.min(100, Math.max(0, (time / DEMO_VIDEO_DURATION_SECONDS) * 100));
  const showUltrasoundPanel = !liveMode || liveUltrasoundAvailable === true;
  const trainingButtonVisible = liveMode && liveFeed && trainingBurst?.available;
  const cameraActionBlocked = liveMode && liveFeed && liveCameraOnline !== true;
  const trainingTarget = trainingBurst?.target ?? liveFeed?.training?.target ?? 50;
  const trainingCount = trainingBurst?.count ?? 0;
  const trainingActive = Boolean(trainingBurst?.active);
  const showLiveSidePanel = !liveMode || liveFeedViewMode === 'focus';
  const onlineCameraCount = liveFeeds.filter((feed) => cameraFeedOnline(feed, liveNowMs)).length;
  const sensorValueCount = metricRows.filter((metric) => Number.isFinite(metric.value)).length;
  const environmentPreview = metricRows
    .filter((metric) => ['temperature', 'ph', 'oxygen'].includes(metric.key) && Number.isFinite(metric.value))
    .slice(0, 3);
  const environmentPreviewLabel = environmentPreview.length
    ? environmentPreview.map((metric) => `${metric.label}: ${metric.value.toFixed(metric.decimals)}${metric.unit}`).join(' · ')
    : 'Sem leituras';
  const alertPreviewLabel = activeAlerts.length === 0
    ? 'Sem alertas'
    : activeCriticalAlertCount > 0
      ? `${activeCriticalAlertCount} crítico${activeCriticalAlertCount > 1 ? 's' : ''}`
      : `${activeAlerts.length} atenção`;

  return (
    <div className={embedded ? "text-foreground" : "min-h-screen bg-background text-foreground"}>
      {!embedded ? (
      <header className="sticky top-0 z-40 border-b border-border bg-background/92 px-4 py-3 backdrop-blur-md sm:px-6">
        <div className="mx-auto flex max-w-[1680px] items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            data-demo-tour="camera-back"
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-card text-foreground hover:bg-accent"
            aria-label="Voltar"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-primary/25 bg-primary/10 p-1.5">
            <AqlVisionMark className="h-full w-full object-contain" />
          </div>
          <div className="min-w-0">
            <h1 className="text-base font-semibold leading-tight sm:text-lg">AQL Vision · Live</h1>
            <p className="text-xs text-muted-foreground sm:text-sm">
              Pregado em água salgada · Tanque T-04 · {showUltrasoundPanel ? 'Câmara + Sonar US' : 'Câmara'}
            </p>
          </div>
          <div className="ml-auto hidden items-center gap-2 md:flex">
            <StatusPill level={overallLevel}>
              {overallLevel === 'normal' ? 'Operação estável' : overallLevel === 'critical' ? 'Alerta crítico' : 'Atenção operacional'}
            </StatusPill>
            <span className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-xs font-semibold ${
              liveMode && liveDeviceOnline === false
                ? 'border-red-300 bg-red-50 text-red-800 dark:border-red-500/35 dark:bg-red-500/10 dark:text-red-100'
                : 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/35 dark:bg-emerald-500/10 dark:text-emerald-100'
            }`}>
              {liveMode && liveDeviceOnline === false ? <WifiOff className="h-3.5 w-3.5" /> : <Wifi className="h-3.5 w-3.5" />}
              {liveMode && liveDeviceOnline === false ? 'Device offline' : 'Device online'}
            </span>
          </div>
        </div>
      </header>
      ) : null}

      <main className={embedded
        ? `grid gap-4 ${showLiveSidePanel ? 'xl:grid-cols-[minmax(0,1fr)_360px]' : ''}`
        : `mx-auto grid max-w-[1680px] gap-4 px-4 py-4 sm:px-6 ${showLiveSidePanel ? 'xl:grid-cols-[minmax(0,1fr)_360px]' : ''}`
      }>
        {/* LEFT: camera feed + ultrasound */}
        <section className="space-y-3">
          {liveMode && liveFeedViewMode === 'grid' ? (
            <div className="aql-vision-dashboard-card flex flex-col gap-3 rounded-xl border bg-background p-4 shadow-sm ring-1 ring-border/35">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-base font-semibold">Unidades de Monitorização</h2>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Estado de comunicação, imagem, ambiente e alertas por edge device.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${
                    liveDeviceOnline === false
                      ? 'border-red-400/35 bg-red-500/10 text-red-200'
                      : 'border-emerald-400/35 bg-emerald-500/10 text-emerald-200'
                  }`}>
                    {liveDeviceOnline === false ? <WifiOff className="h-3.5 w-3.5" /> : <Wifi className="h-3.5 w-3.5" />}
                    {onlineCameraCount}/{Math.max(liveFeeds.length, 1)} online
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/30 px-2.5 py-1 text-xs font-semibold text-muted-foreground">
                    <Thermometer className="h-3.5 w-3.5" />
                    {sensorValueCount} sensores
                  </span>
                  <IconButton
                    size="sm"
                    label="Atualizar dados"
                    onClick={() => {
                      setLiveFeedFailed(false);
                      setLiveFeedTick(Date.now());
                      setLiveRefreshKey((value) => value + 1);
                    }}
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                  </IconButton>
                </div>
              </div>

              {liveFeeds.length ? (
                <div className="mt-4 grid grid-cols-[repeat(auto-fill,minmax(min(100%,300px),360px))] justify-start gap-3">
                  {liveFeeds.map((feed, index) => {
                    const key = liveFeedKey(feed);
                    const selected = key === activeLiveFeedKey;
                    const hasLocalPreview = Boolean(localCameraStream && selected);
                    const online = hasLocalPreview || cameraFeedOnline(feed, liveNowMs);
                    const lastFrameLabel = formatTimeOnly(feed.lastFrameAt);
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => openLiveFeed(feed)}
                        className={`group overflow-hidden rounded-xl border bg-background text-left shadow-sm ring-1 ring-border/35 transition-all duration-200 hover:-translate-y-1 hover:scale-[1.006] hover:ring-2 hover:ring-primary/45 hover:shadow-2xl hover:shadow-primary/12 focus:outline-none focus:ring-2 focus:ring-primary/60 ${
                          selected ? 'border-primary/60 ring-2 ring-primary/35' : 'border-border'
                        }`}
                      >
                        <div className="flex min-h-[340px] flex-col p-3">
                          <div className="relative aspect-video overflow-hidden rounded-lg bg-black">
                            {hasLocalPreview ? (
                              <video
                                ref={localCameraVideoRef}
                                className="h-full w-full object-cover [backface-visibility:hidden] [transform:translateZ(0)]"
                                autoPlay
                                muted
                                playsInline
                                disablePictureInPicture
                                aria-label={`Pré-visualização de ${feed.cameraName ?? feed.cameraCode ?? 'câmara'}`}
                              />
                            ) : (
                              <img
                                src={withCacheBuster(feed.url, liveFeedTick)}
                                alt={feed.cameraName ?? feed.cameraCode ?? `Câmara ${index + 1}`}
                                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.025]"
                                loading="eager"
                                onError={(event) => {
                                  event.currentTarget.style.display = 'none';
                                }}
                              />
                            )}
                            <span className="absolute left-2 top-2 rounded-full border border-white/20 bg-black/60 px-2 py-0.5 text-[9px] font-semibold uppercase text-white backdrop-blur">
                              {hasLocalPreview ? 'Câmara local' : 'Live'}
                            </span>
                          </div>

                          <div className="mt-3 min-w-0 flex-1 space-y-2">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="truncate text-sm font-bold">
                                  {feed.cameraName ?? feed.cameraCode ?? `Unidade ${index + 1}`}
                                </p>
                                <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{feed.cameraCode ?? feed.path ?? 'código a confirmar'}</p>
                              </div>
                              <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
                                online
                                  ? 'border-emerald-300/35 bg-emerald-500/10 text-emerald-700 dark:text-emerald-200'
                                  : 'border-red-300/45 bg-red-500/10 text-red-700 dark:text-red-200'
                              }`}>
                                {hasLocalPreview ? 'Local ativa' : online ? 'Online' : 'Offline'}
                              </span>
                            </div>

                            <div className="grid gap-1 text-[11px]">
                              <div className="grid min-w-0 grid-cols-[4.75rem_minmax(0,1fr)] items-center gap-2 rounded-md bg-muted/30 px-2 py-1">
                                <span className="inline-flex min-w-0 items-center gap-1 text-muted-foreground">
                                  <Images className="h-3 w-3" />
                                  Imagem
                                </span>
                                <span className="min-w-0 truncate text-right font-semibold text-foreground" title={lastFrameLabel ? `frame ${lastFrameLabel}` : 'sem frame recente'}>
                                  {lastFrameLabel ? `frame ${lastFrameLabel}` : 'sem frame'}
                                </span>
                              </div>
                              <div className="grid min-w-0 grid-cols-[4.75rem_minmax(0,1fr)] items-center gap-2 rounded-md bg-muted/30 px-2 py-1">
                                <span className="inline-flex min-w-0 items-center gap-1 text-muted-foreground">
                                  <Thermometer className="h-3 w-3" />
                                  Ambiente
                                </span>
                                <span className="min-w-0 truncate text-right font-semibold text-foreground" title={environmentPreviewLabel}>
                                  {environmentPreview.length ? `${sensorValueCount} leituras` : 'sem leituras'}
                                </span>
                              </div>
                              <div className="grid min-w-0 grid-cols-[4.75rem_minmax(0,1fr)] items-center gap-2 rounded-md bg-muted/30 px-2 py-1">
                                <span className="inline-flex min-w-0 items-center gap-1 text-muted-foreground">
                                  {activeAlerts.length === 0 ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
                                  Alertas
                                </span>
                                <span className={`min-w-0 truncate text-right font-semibold ${
                                  activeAlerts.length === 0
                                    ? 'text-emerald-700 dark:text-emerald-200'
                                    : activeCriticalAlertCount > 0
                                      ? 'text-red-700 dark:text-red-200'
                                      : 'text-amber-700 dark:text-amber-200'
                                }`} title={alertPreviewLabel}>{alertPreviewLabel}</span>
                              </div>
                            </div>

                            <div className="flex items-center justify-between gap-2 pt-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                              <span className="truncate" title={environmentPreviewLabel}>{feed.isPrimary ? 'Principal' : 'Edge device'}</span>
                              <span className="inline-flex items-center gap-1 text-primary">
                                Abrir
                                <Maximize2 className="h-3 w-3" />
                              </span>
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="mt-4 flex min-h-[220px] items-center justify-center rounded-lg border border-dashed border-border bg-background/40 px-4 text-center text-sm font-medium text-muted-foreground">
                  {liveError ?? 'Este kit ainda não tem câmaras com feed configurado. Quando existirem câmaras associadas, aparecem aqui para entrar na vista live.'}
                </div>
              )}
            </div>
          ) : (
            <div data-demo-tour="live-camera" className="overflow-hidden rounded-lg border border-border bg-card shadow-sm dark:bg-slate-950">
              <div className={`relative bg-black dark:bg-slate-950 ${
                liveMode
                  ? 'h-[min(68vh,760px)] min-h-[320px] w-full sm:min-h-[460px]'
                  : 'mx-auto aspect-[9/16] max-h-[62vh]'
              }`}>
              {liveMode ? (
                localCameraStream ? (
                  <video
                    ref={localCameraVideoRef}
                    className="h-full w-full object-contain [backface-visibility:hidden] [transform:translateZ(0)]"
                    autoPlay
                    muted
                    playsInline
                    disablePictureInPicture
                    aria-label="Câmara deste dispositivo"
                  />
                ) : liveFeedFrameUrl ? (
                  <img
                    ref={liveImageRef}
                    crossOrigin="anonymous"
                    src={liveFeedFrameUrl}
                    alt="Feed em direto"
                    className="h-full w-full object-contain transition-opacity duration-300"
                    onLoad={() => setLiveFeedFailed(false)}
                    onError={() => {
                      if (!liveFeedDisplayUrlRef.current) setLiveFeedFailed(true);
                    }}
                  />
                ) : liveCapture?.image_url ? (
                  <img
                    src={withCacheBuster(liveCapture.image_url, liveFeedTick)}
                    alt="Última captura"
                    className="h-full w-full object-contain transition-opacity duration-300"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center px-6 text-center">
                    <div className="max-w-md">
                      <Camera className="mx-auto h-10 w-10 text-sky-300" />
                      <p className="mt-4 text-sm font-semibold text-slate-100">Sem feed publicado pelo Edge</p>
                      <p className="mt-2 text-xs leading-5 text-slate-400">
                        Usa a câmara deste telemóvel, tablet ou computador para apresentar o vídeo e validar o modelo ONNX localmente.
                      </p>
                      <button
                        type="button"
                        onClick={() => void startLocalCamera()}
                        disabled={localCameraStarting || typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia}
                        className="mt-5 inline-flex h-11 items-center justify-center rounded-xl bg-sky-400 px-5 text-sm font-bold text-slate-950 transition hover:bg-sky-300 disabled:cursor-not-allowed disabled:opacity-55"
                      >
                        {localCameraStarting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Camera className="mr-2 h-4 w-4" />}
                        {localCameraStarting ? 'A ligar câmara…' : 'Usar câmara deste dispositivo'}
                      </button>
                      {localCameraError ? <p className="mt-3 text-xs font-medium text-red-300">{localCameraError}</p> : null}
                    </div>
                  </div>
                )
              ) : (
                <video
                  ref={videoRef}
                  poster={DEMO_POSTER_SRC}
                  className="h-full w-full object-contain transition-opacity duration-500"
                  autoPlay
                  muted
                  playsInline
                  loop
                  controls={false}
                  disablePictureInPicture
                  onCanPlay={(event) => {
                    setVideoReady(true);
                    setVideoError(null);
                    event.currentTarget.play().catch(() => undefined);
                  }}
                  onPlay={() => setPlaying(true)}
                  onPause={() => setPlaying(false)}
                  onLoadedMetadata={(event) => seekToInitialDetection(event.currentTarget)}
                  onError={(event) => {
                    const error = event.currentTarget.error;
                    const src = event.currentTarget.currentSrc || DEMO_VIDEO_MP4_SRC;
                    const label = error ? `Código ${error.code}` : 'Erro de rede';
                    setVideoReady(false);
                    setVideoError(`${label} · ${src.split('/').pop()}`);
                  }}
                  onTimeUpdate={(event) => {
                    setTime(event.currentTarget.currentTime || 0);
                    const video = event.currentTarget;
                    if (video.duration) {
                      const timeLeft = video.duration - video.currentTime;
                      if (timeLeft < 0.8) {
                        video.style.opacity = '0';
                      } else if (video.currentTime > 0.2 && video.style.opacity === '0') {
                        video.style.opacity = '1';
                      }
                    }
                  }}
                  onVolumeChange={(event) => {
                    event.currentTarget.muted = true;
                    event.currentTarget.volume = 0;
                  }}
                >
                  <source src={DEMO_VIDEO_MP4_SRC} type="video/mp4" />
                  <source src={DEMO_VIDEO_WEBM_SRC} type="video/webm" />
                </video>
              )}

              {liveMode && browserInference ? browserInference.detections.map((detection, index) => (
                <div key={`${detection.label}-${index}`} className="pointer-events-none absolute z-10 border-2 border-cyan-400 shadow-[0_0_0_1px_rgba(0,0,0,0.65)]" style={{ left: `${detection.x * 100}%`, top: `${detection.y * 100}%`, width: `${detection.width * 100}%`, height: `${detection.height * 100}%` }}>
                  <span className="absolute -top-6 left-0 whitespace-nowrap rounded bg-cyan-500 px-1.5 py-0.5 text-[10px] font-bold text-slate-950">{detection.label} · {Math.round(detection.confidence * 100)}%</span>
                </div>
              )) : null}

              {liveMode && !localCameraStream && edgeInference && edgeInference.frame.width > 0 && edgeInference.frame.height > 0 ? (
                <svg
                  className="pointer-events-none absolute inset-0 z-10 h-full w-full"
                  viewBox={`0 0 ${edgeInference.frame.width} ${edgeInference.frame.height}`}
                  preserveAspectRatio="xMidYMid meet"
                  aria-label={`${edgeInference.detections.length} deteções executadas no Edge`}
                >
                  {edgeInference.detections.map((detection, index) => {
                    const label = `${detection.class_label} · ${Math.round(detection.confidence * 100)}%`;
                    const fontSize = Math.max(18, edgeInference.frame.height * 0.025);
                    const labelWidth = Math.max(110, label.length * fontSize * 0.58);
                    const labelY = Math.max(fontSize + 8, detection.bbox.y);
                    return (
                      <g key={`${detection.class_label}-${index}`}>
                        <rect
                          x={detection.bbox.x}
                          y={detection.bbox.y}
                          width={detection.bbox.width}
                          height={detection.bbox.height}
                          fill="none"
                          stroke="#22d3ee"
                          strokeWidth={Math.max(3, edgeInference.frame.width / 500)}
                        />
                        <rect
                          x={detection.bbox.x}
                          y={labelY - fontSize - 8}
                          width={labelWidth}
                          height={fontSize + 10}
                          rx={5}
                          fill="#06b6d4"
                        />
                        <text
                          x={detection.bbox.x + 6}
                          y={labelY - 5}
                          fill="#082f49"
                          fontSize={fontSize}
                          fontWeight="700"
                        >
                          {label}
                        </text>
                      </g>
                    );
                  })}
                </svg>
              ) : null}

              {liveMode ? (
                <div className="absolute inset-x-3 top-3 z-20 flex items-start justify-between gap-3">
                  <div className="pointer-events-none flex min-w-0 flex-wrap items-center gap-2">
                    {liveFeed ? (
                      <span className={`inline-flex h-9 items-center rounded-full border px-3 text-[11px] font-semibold shadow-lg backdrop-blur ${
                        browserInferenceError
                          ? 'border-amber-300/40 bg-amber-950/75 text-amber-100'
                          : 'border-white/20 bg-black/70 text-white'
                      }`}>
                        {liveFeed.inference.mode === 'browser-onnx' && localCameraStream
                          ? browserInferenceError
                            ? 'ONNX indisponível'
                            : `ONNX local${liveFeed.inference.version ? ` · v${liveFeed.inference.version}` : ''}${browserInference ? ` · ${Math.round(browserInference.latencyMs)} ms` : ' · a iniciar'}`
                          : liveFeed.kitId || liveFeed.inference.mode === 'edge-pytorch'
                            ? edgeInference
                              ? `ONNX no Edge · v${edgeInference.model?.version ?? '—'}`
                              : 'ONNX no Edge · a aguardar'
                            : 'Vídeo sem inferência'}
                      </span>
                    ) : null}
                    <span className="inline-flex h-9 items-center gap-1.5 rounded-full border border-sky-400/40 bg-black/70 px-3 text-[11px] font-semibold text-sky-100 shadow-lg backdrop-blur">
                      <Fish className="h-3.5 w-3.5" />
                      {(browserInference?.detections.length ?? edgeInference?.detections.length ?? liveDetectionCount ?? 0) === 0
                        ? 'Sem deteções'
                        : `${browserInference?.detections.length ?? edgeInference?.detections.length ?? liveDetectionCount ?? 0} deteções`}
                    </span>
                    <span className={`inline-flex h-9 items-center rounded-full border px-3 text-[10px] font-bold uppercase tracking-wide shadow-lg backdrop-blur ${
                      effectiveCameraOnline
                        ? 'border-emerald-300/40 bg-emerald-950/70 text-emerald-100'
                        : 'border-red-300/40 bg-red-950/70 text-red-100'
                    }`}>
                      {localCameraStream ? 'Câmara local' : effectiveCameraOnline ? 'Live' : 'Offline'}
                    </span>
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    {localCameraStream ? <>
                      {localVideoInputs.length > 1 ? <select
                        value={selectedLocalCameraId}
                        onChange={(event) => void startLocalCamera(event.target.value)}
                        disabled={localCameraStarting}
                        className="h-9 w-[clamp(9rem,20vw,13rem)] truncate rounded-full border border-white/20 bg-black/75 px-3 text-[11px] font-semibold text-white shadow-lg outline-none backdrop-blur focus:border-sky-300 disabled:opacity-60"
                        aria-label="Escolher câmara deste dispositivo"
                      >
                        {localVideoInputs.map((device, index) => <option key={device.deviceId} value={device.deviceId}>{`Câmara ${index}`}</option>)}
                      </select> : null}
                      <button
                        type="button"
                        onClick={stopLocalCamera}
                        className="inline-flex h-9 items-center justify-center rounded-full border border-red-300/30 bg-black/70 px-3 text-[11px] font-semibold text-red-100 shadow-lg backdrop-blur transition hover:bg-red-500/80 focus:outline-none focus:ring-2 focus:ring-red-400/60"
                      >
                        Parar
                      </button>
                    </> : null}
                  <button
                    type="button"
                    onClick={() => setLiveFeedViewMode('grid')}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/20 bg-black/70 text-white shadow-lg backdrop-blur transition hover:bg-primary hover:text-primary-foreground focus:outline-none focus:ring-2 focus:ring-primary/60"
                    aria-label="Voltar às câmaras"
                    title="Voltar às câmaras"
                  >
                    <Minimize2 className="h-4 w-4" />
                  </button>
                </div>
                </div>
              ) : null}

              {liveMode && !localCameraStream ? (
                <div className="absolute bottom-3 left-3 z-20 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setLiveFeedFailed(false);
                      setLiveFeedTick(Date.now());
                      setLiveRefreshKey((value) => value + 1);
                    }}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-black/60 text-white shadow-lg backdrop-blur transition hover:bg-primary hover:text-primary-foreground focus:outline-none focus:ring-2 focus:ring-primary/60"
                    aria-label="Refrescar feed agora"
                    title="Refrescar feed agora"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </button>
                </div>
              ) : null}

              {liveMode && !localCameraStream && liveFeedBuffering && !liveFeedPrimed ? (
                <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/72 backdrop-blur-sm">
                  <div className="flex items-center gap-3 rounded-full border border-sky-300/25 bg-slate-950/80 px-4 py-2 text-xs font-semibold text-sky-100 shadow-lg">
                    <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-sky-300" />
                    A preparar buffer do feed
                  </div>
                </div>
              ) : null}

              {!liveMode && !videoReady && videoError ? (
                <div className="absolute inset-0 flex flex-col items-center justify-end">
                  {DEMO_POSTER_SRC ? (
                    <img
                      src={DEMO_POSTER_SRC}
                      alt="Última captura"
                      className="absolute inset-0 h-full w-full object-cover opacity-40"
                    />
                  ) : null}
                  <div className="relative z-10 w-full border-t border-amber-400/20 bg-amber-500/10 px-4 py-3 text-center backdrop-blur-sm">
                    <p className="text-xs font-semibold text-amber-200">Vídeo indisponível · {videoError}</p>
                    <p className="mt-0.5 text-[10px] text-amber-300/70">Funcionalidade completa na aplicação desktop</p>
                  </div>
                </div>
              ) : null}

              {/* Demo-only camera metadata */}
              {!liveMode ? <div className="pointer-events-none absolute left-3 top-3 flex flex-wrap gap-1.5">
                <span className="rounded-full border border-white/20 bg-black/55 px-2.5 py-1 text-xs font-semibold text-white backdrop-blur">
                  {liveMode
                    ? `${liveFeed?.cameraName ?? liveZone?.zoneCode ?? liveZone?.zoneName ?? 'CAM'} · ${
                        liveFeedFrameUrl
                          ? new Date(liveFeedFrameLoadedAt ?? liveFeedTick).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
                          : liveCapture
                            ? new Date(liveCapture.captured_at).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
                            : '—'
                      }`
                    : `CAM-04 · ${formatClock(time)}`}
                </span>
                {liveMode ? (
                  <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold backdrop-blur ${
                    effectiveCameraOnline
                      ? 'border-emerald-300/40 bg-emerald-500/15 text-emerald-200'
                      : 'border-red-300/45 bg-red-500/20 text-red-100'
                  }`}>
                    {localCameraStream ? 'CÂMARA LOCAL' : effectiveCameraOnline ? 'LIVE' : 'OFFLINE'}
                  </span>
                ) : (
                  <span className="rounded-full border border-white/20 bg-black/55 px-2.5 py-1 text-xs font-semibold text-white backdrop-blur">
                    Sem áudio
                  </span>
                )}
              </div> : null}

              {/* Demo-only inference summary */}
              {!liveMode ? <div className="pointer-events-none absolute left-1/2 top-3 flex -translate-x-1/2 flex-col items-center gap-1.5">
                <span className="flex items-center gap-1.5 rounded-full border border-sky-400/40 bg-black/60 px-2.5 py-1 text-xs font-semibold text-sky-200 backdrop-blur">
                  <Fish className="h-3.5 w-3.5" />
                  {liveMode && liveDetectionCount === null
                    ? 'sem deteções'
                    : `~${fishEstimate.count} espécimes`}
                </span>
                <span className="rounded-full border border-white/15 bg-black/50 px-2 py-0.5 text-[10px] font-medium text-slate-300 backdrop-blur">
                  {liveMode ? 'IA · contagem por captura' : `IA est. · ${fishEstimate.confidence}% conf.`}
                </span>
              </div> : null}

              {visualEvent ? (
                <div
                  className="pointer-events-none absolute rounded-lg border-2 border-red-400 bg-red-500/10 shadow-[0_0_0_9999px_rgba(2,6,23,0.18)]"
                  style={visualEvent.box as CSSProperties}
                >
                  <span className="absolute -top-8 left-0 whitespace-nowrap rounded-md bg-red-600 px-2 py-1 text-xs font-semibold text-white shadow">
                    {visualEvent.confidence}%
                  </span>
                </div>
              ) : null}

              {/* Progress bar — video-only */}
              {liveMode ? null : (
                <div className="pointer-events-none absolute inset-x-0 bottom-0 p-3">
                  <div className="h-1.5 overflow-hidden rounded-full bg-white/18">
                    <div className="h-full rounded-full bg-sky-400" style={{ width: `${progress}%` }} />
                  </div>
                </div>
              )}
            </div>

            {/* Controls — single compact row */}
            <div className="flex flex-wrap items-center gap-2 border-t border-border bg-card px-3 py-2.5 dark:border-white/10 dark:bg-slate-950">
              {liveMode ? (
                <>
                  {liveFeeds.length > 1 ? (
                    <select
                      value={liveFeed ? liveFeedKey(liveFeed) : ''}
                      onChange={(event) => {
                        const next = liveFeeds.find((feed) => liveFeedKey(feed) === event.target.value) ?? liveFeeds[0] ?? null;
                        if (next) openLiveFeed(next);
                      }}
                      className="h-8 max-w-[220px] rounded-md border border-border bg-background px-2 text-xs font-semibold text-foreground outline-none transition-colors hover:bg-accent focus:border-primary"
                      aria-label="Selecionar câmara"
                    >
                      {liveFeeds.map((feed, index) => {
                        const key = liveFeedKey(feed);
                        return (
                          <option key={key} value={key}>
                            {feed.cameraName ?? feed.cameraCode ?? `Câmara ${index + 1}`}
                            {feed.isPrimary ? ' · principal' : ''}
                          </option>
                        );
                      })}
                    </select>
                  ) : null}
                  {trainingButtonVisible ? (
                    <button
                      type="button"
                      onClick={toggleTrainingBurst}
                      disabled={trainingBurstBusy || Boolean(cameraActionBlocked)}
                      className={`inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                        cameraActionBlocked
                          ? 'border-border bg-muted/40 text-muted-foreground'
                          : trainingActive
                          ? 'border-amber-300/45 bg-amber-400/10 text-amber-700 hover:bg-amber-400/15 dark:text-amber-200'
                          : 'border-border bg-muted/30 text-foreground hover:border-primary/45 hover:bg-accent'
                      }`}
                      title={
                        cameraActionBlocked
                          ? 'Câmara offline: não é possível pedir imagens'
                          : trainingActive
                            ? 'Parar recolha de frames para treino'
                            : `Recolher ${trainingTarget} frames para treino`
                      }
                    >
                      {trainingActive ? <Pause className="h-3.5 w-3.5" /> : <ScanLine className="h-3.5 w-3.5" />}
                      {cameraActionBlocked
                        ? 'Câmara offline'
                        : trainingBurstBusy
                        ? 'A atualizar...'
                        : trainingActive
                          ? `A recolher ${trainingCount}/${trainingTarget}`
                          : 'Recolher dados'}
                    </button>
                  ) : null}
                  {cameraActionBlocked && !localCameraStream ? (
                    <span className="inline-flex h-8 items-center rounded-md border border-red-300/35 bg-red-500/10 px-2.5 text-xs font-semibold text-red-700 dark:text-red-200">
                      Ações de imagem bloqueadas
                    </span>
                  ) : null}
                  <span className="text-[11px] text-muted-foreground">
                    {localCameraStream
                      ? 'Câmara deste dispositivo · inferência local no browser'
                      : `Feed ${liveFeed ? liveFeed.path : 'feed.jpg'} · browser ${LIVE_FEED_FRAME_REFRESH_MS}ms · metadata ${LIVE_SENSORS_REFRESH_MS / 1000}s`}
                  </span>
                  <span className="ml-auto text-[11px] text-muted-foreground">
                    {localCameraStream
                      ? browserInference
                        ? `Vídeo local ativo · inferência ${Math.round(browserInference.latencyMs)} ms`
                        : 'Vídeo local ativo · modelo a iniciar'
                      : liveFeedFrameUrl
                      ? liveFeed && liveCameraOnline
                        ? `Frame recente · ${formatTimeOnly(liveFeed.lastFrameAt) ?? formatTimeOnly(new Date(liveFeedFrameLoadedAt ?? liveFeedTick).toISOString())}`
                        : liveFeed?.lastFrameAt
                          ? `Frame antigo · ${formatTimeOnly(liveFeed.lastFrameAt)}`
                          : 'Imagem guardada · sem heartbeat de câmara'
                      : liveFeedFailed
                        ? 'Feed indisponível · a usar última captura'
                        : liveCapture
                          ? `Última captura · ${new Date(liveCapture.captured_at).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}`
                          : 'À espera de captura…'}
                  </span>
                </>
              ) : (
                <>
                  <div data-demo-tour="simulation-controls" className="flex items-center gap-1.5">
                    <IconButton size="sm" label={playing ? 'Pausar' : 'Reproduzir'} onClick={togglePlayback} active={playing}>
                      {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                    </IconButton>
                    <IconButton size="sm" label="Reiniciar" onClick={resetDemo}>
                      <RotateCcw className="h-3.5 w-3.5" />
                    </IconButton>
                    <IconButton size="sm" label="Deteção anterior" onClick={jumpToPreviousDetection}>
                      <SkipBack className="h-3.5 w-3.5" />
                    </IconButton>
                    <IconButton size="sm" label="Próxima deteção" onClick={jumpToNextDetection}>
                      <SkipForward className="h-3.5 w-3.5" />
                    </IconButton>
                  </div>

                  <input
                    type="range"
                    min={0}
                    max={DEMO_VIDEO_DURATION_SECONDS}
                    step={0.1}
                    value={Math.min(time, DEMO_VIDEO_DURATION_SECONDS)}
                    onChange={(event) => jumpTo(Number(event.target.value), false)}
                    className="min-w-0 flex-1 accent-sky-400"
                    aria-label="Linha temporal"
                  />

                  <div className="flex items-center gap-1.5">
                    <IconButton size="sm" label="Simular pico térmico" onClick={() => inject('thermal')} active={injection === 'thermal'}>
                      <Thermometer className="h-3.5 w-3.5" />
                    </IconButton>
                    <IconButton size="sm" label="Simular queda O₂" onClick={() => inject('oxygen')} active={injection === 'oxygen'}>
                      <Zap className="h-3.5 w-3.5" />
                    </IconButton>
                    <IconButton size="sm" label="Simular falha sensor" onClick={() => inject('sensor')} active={injection === 'sensor'}>
                      <CircleGauge className="h-3.5 w-3.5" />
                    </IconButton>
                  </div>
                </>
              )}
            </div>
          </div>
          )}

          {showUltrasoundPanel && (!liveMode || liveFeedViewMode === 'focus') ? (
            <UltrasoundPanel time={time} cameraCount={fishEstimate.count} demoMode={demoMode} />
          ) : null}
        </section>

        {/* RIGHT: sensors + device status + alerts + detection feed */}
        {showLiveSidePanel ? (
        <aside className="space-y-3">
          {/* Device status strip */}
          <div className="grid grid-cols-2 gap-2">
            <div className={`min-w-0 rounded-lg border px-3 py-2.5 shadow-sm ${
              liveMode && liveDeviceOnline === false
                ? 'border-red-500/35 bg-red-500/10'
                : 'border-border bg-card'
            }`}>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Device</p>
              <div className={`mt-1 flex items-center gap-1.5 ${
                liveMode && liveDeviceOnline === false
                  ? 'text-red-700 dark:text-red-300'
                  : 'text-emerald-700 dark:text-emerald-300'
              }`}>
                {liveMode && liveDeviceOnline === false ? <WifiOff className="h-3.5 w-3.5 shrink-0" /> : <Wifi className="h-3.5 w-3.5 shrink-0" />}
                <span className="text-sm font-bold">{liveMode && liveDeviceOnline === false ? 'Offline' : 'Online'}</span>
              </div>
              <p className="mt-1 truncate font-mono text-xs font-semibold tabular-nums" title={deviceLastContact}>
                {deviceLastContact}
              </p>
              <p className="mt-0.5 truncate text-[10px] text-muted-foreground">
                {liveEdgeAgentVersion
                  ? `Agente v${liveEdgeAgentVersion} · Realtime`
                  : liveDevicePresence?.agentVersion
                    ? `Agente v${liveDevicePresence.agentVersion} · heartbeat`
                    : 'Agente sem versão · heartbeat'}
              </p>

            </div>
            
            <div className={`min-w-0 rounded-lg border px-3 py-2.5 shadow-sm ${sensorsOk ? levelTone('normal') : levelTone('attention')}`}>
              <p className="text-[10px] font-semibold uppercase tracking-wide opacity-70">Sensores</p>
              <div className="mt-1 flex items-center gap-1.5">
                {sensorsOk ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> : <AlertTriangle className="h-3.5 w-3.5 shrink-0" />}
                <span className="text-sm font-bold">{sensorsOk ? 'OK' : 'Verificar'}</span>
              </div>
            </div>
          </div>

          {/* Environmental metrics — compact rows */}
          <section data-demo-tour="environment-sensors" className="rounded-lg border border-border bg-card shadow-sm">
            <div className="border-b border-border px-3 py-2.5">
              <h2 className="text-sm font-semibold">Sensores ambientais</h2>
            </div>
            <div className="divide-y divide-border">
              {metricRows.map((metric) => {
                const Icon = metric.icon;
                return (
                  <div key={metric.key} className={`flex items-center justify-between px-3 py-2.5 ${metric.level !== 'normal' ? (metric.level === 'critical' ? 'bg-red-500/5 dark:bg-red-500/8' : 'bg-amber-500/5 dark:bg-amber-500/8') : ''}`}>
                    <div className="flex items-center gap-2">
                      <Icon className={`h-4 w-4 shrink-0 ${metric.color}`} />
                      <div>
                        <p className="text-sm font-semibold">{metric.label}</p>
                        <p className="text-[10px] text-muted-foreground">{metric.target}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-base font-bold ${metric.level === 'critical' ? 'text-red-600 dark:text-red-400' : metric.level === 'attention' ? 'text-amber-600 dark:text-amber-400' : 'text-foreground'}`}>
                        {Number.isFinite(metric.value) ? metric.value.toFixed(metric.decimals) : '—'}
                        {metric.unit && Number.isFinite(metric.value) ? <span className="ml-0.5 text-xs font-semibold opacity-70">{metric.unit}</span> : null}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Alerts */}
          <section data-demo-tour="live-alerts" className="rounded-lg border border-border bg-card p-4 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold">Alertas ativos</h2>
                <p className="text-sm text-muted-foreground">{activeAlerts.length} em validação</p>
              </div>
              <StatusPill level={overallLevel}>{overallLevel === 'normal' ? 'OK' : overallLevel === 'critical' ? 'Crítico' : 'Atenção'}</StatusPill>
            </div>

            <div className="mt-4 space-y-2">
              {activeAlerts.length ? activeAlerts.map((alert) => (
                <button
                  key={alert.id}
                  type="button"
                  onClick={() => onOpenAlerts?.(alert.id)}
                  className={`w-full rounded-lg border p-3 text-left transition-colors hover:border-primary/40 hover:bg-accent ${levelTone(alert.level)}`}
                >
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <div className="min-w-0">
                      <p className="font-semibold leading-snug">{alert.title}</p>
                      <p className="mt-1 text-xs opacity-80">{alert.detail}</p>
                      <p className="mt-2 text-xs font-semibold opacity-70">{alert.timestamp}</p>
                    </div>
                  </div>
                </button>
              )) : (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm font-semibold text-emerald-800 dark:border-emerald-500/35 dark:bg-emerald-500/10 dark:text-emerald-100">
                  Sem alertas ativos neste instante.
                </div>
              )}
            </div>
          </section>

          {/* Detection feed */}
          <section className="rounded-lg border border-border bg-card p-4 shadow-sm">
            <h2 className="text-base font-semibold">Feed em tempo real</h2>
            <div className="mt-4 space-y-2">
              {liveMode ? (
                <div className="rounded-lg border border-border bg-muted/40 p-4 text-sm font-medium text-muted-foreground">
                  Sem eventos de inferência ainda. Quando o modelo começar a publicar deteções reais, elas aparecem aqui.
                </div>
              ) : VISUAL_EVENTS.map((event) => {
                const state = time >= event.start && time <= event.end
                  ? 'active'
                  : time > event.end
                    ? 'past'
                    : 'future';
                const stateLevel: AlertLevel = state === 'active' ? 'attention' : 'normal';
                return (
                  <button
                    key={event.id}
                    type="button"
                    onClick={() => onOpenAlerts?.(event.id) ?? jumpTo(event.start + 0.15)}
                    className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors hover:border-primary/40 hover:bg-accent ${state === 'active' ? levelTone(stateLevel) : 'border-border bg-muted'}`}
                  >
                    <ScanLine className="h-4 w-4 shrink-0 text-primary" />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold">{event.title}</span>
                      <span className="block text-xs text-muted-foreground">{formatClock(event.start)} · confiança {event.confidence}%</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        </aside>
        ) : null}
      </main>

      {!embedded ? (
      <AqlOperationalContextProvider value={assistantContextValue}>
        <AqlAssistantButton
          open={assistantOpen}
          criticalCount={activeCriticalAlertCount}
          onClick={() => setAssistantOpen((value) => !value)}
        />
        <AqlAssistantPanel
          open={assistantOpen}
          onClose={() => setAssistantOpen(false)}
          authToken={DEMO_AUTH_TOKEN}
          summary={assistantSummary}
          demoMode
        />
      </AqlOperationalContextProvider>
      ) : null}

      <Dialog
        open={Boolean(trainingExportPrompt)}
        onOpenChange={(open) => {
          if (!open && !trainingExportAction) setTrainingExportPrompt(null);
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <div className="mb-1 flex h-10 w-10 items-center justify-center rounded-lg border border-sky-300/35 bg-sky-500/10 text-sky-600 dark:text-sky-200">
              <Images className="h-5 w-5" />
            </div>
            <DialogTitle>Recolha de dados parada</DialogTitle>
            <DialogDescription>
              Foram guardadas {trainingExportPrompt?.count ?? 0} imagens no Supabase. Podes descarregar um ZIP local ou partilhar a recolha.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-md border border-border bg-muted/35 px-3 py-2 text-xs text-muted-foreground">
            <p className="font-semibold text-foreground">
              {trainingExportPrompt?.cameraName ?? trainingExportPrompt?.cameraCode ?? 'Câmara ao vivo'}
            </p>
            <p className="mt-1 break-all font-mono">
              {trainingExportPrompt?.folderPath ?? 'captures/live/.../training'}
            </p>
            {trainingExportProgress ? (
              <p className="mt-2 font-semibold text-sky-700 dark:text-sky-200">
                {trainingExportProgress.completed}/{trainingExportProgress.total} imagens preparadas
              </p>
            ) : null}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setTrainingExportPrompt(null)}
              disabled={Boolean(trainingExportAction)}
            >
              Agora não
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleShareTrainingDataset}
              disabled={Boolean(trainingExportAction)}
            >
              {trainingExportAction === 'share' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
              Partilhar
            </Button>
            <Button
              type="button"
              onClick={handleDownloadTrainingDataset}
              disabled={Boolean(trainingExportAction)}
            >
              {trainingExportAction === 'download' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Descarregar ZIP
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
